import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Paper,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  LinearProgress,
  Button,
  Popover,
  TextField,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Chat as ChatIcon,
  AttachMoney as AttachMoneyIcon,
  Schedule as ScheduleIcon,
  Assessment as AssessmentIcon,
  Refresh as RefreshIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  Search as SearchIcon,
  WhatsApp as WhatsAppIcon,
  Telegram as TelegramIcon,
  Instagram as InstagramIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  AccessTime as AccessTimeIcon,
  Error as ErrorIcon,
  Speed as SpeedIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Star as StarIcon,
  CalendarToday as CalendarIcon,
  DateRange as DateRangeIcon,
  Favorite as FavoriteIcon,
  LocalHospital as LocalHospitalIcon,
  GroupAdd as GroupAddIcon,
  PersonAdd as PersonAddIcon,
  Assignment as AssignmentIcon,
  Healing as HealingIcon,
  MedicalServices as MedicalServicesIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Area,
  AreaChart,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import axios from '../config/axios';
import { API_CONFIG } from '../config/api';
import { getApiUrl, config } from '../config/environment';
import { getTodayDates, getLastDaysDates, getYesterdayDates } from '../utils/dateFilters';
import { useDashboardOptimization } from '../utils/dashboardOptimizations';
import agendaService, { Event } from '../services/agendaService';
import { CardSpotlight } from '../components/ui/CardSpotlight';

interface DashboardMetrics {
  conversations: {
    incoming: number;
    inQueue: number;
    ongoing: number;
    completed: number;
    abandoned: number;
  };
  performance: {
    avgSpeedToAnswer: string;
    avgHandleTime: string;
    longestWaitTime: string;
    serviceLevel: number;
    abandonedRate: number;
  };
  channels: {
    whatsapp: number;
    telegram: number;
    instagram: number;
    sms: number;
  };
  agents: {
    total: number;
    available: number;
    busy: number;
    away: number;
    offline: number;
  };

}

interface ConversationData {
  time: string;
  conversations: number;
}

interface ChannelData {
  channel: string;
  open: number;
  active: number;
  wrapup: number;
}

interface AgentData {
  name: string;
  status: 'available' | 'busy' | 'away' | 'offline';
  conversations: number;
  avgResponseTime: string;
}

interface RecentConversation {
  id: string;
  contactName: string;
  channel: string;
  status: 'active' | 'closed' | 'pending';
  lastMessage: string;
  lastMessageTime: string;
  agentName?: string;
}

interface DailyConversations {
  date: string;
  conversations: number;
}

const Analytics: React.FC = () => {
  // Dashboard Analytics carregado
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autoUpdate, setAutoUpdate] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    conversations: {
      incoming: 0,
      inQueue: 0,
      ongoing: 0,
      completed: 0,
      abandoned: 0,
    },
    performance: {
      avgSpeedToAnswer: '00:00:00',
      avgHandleTime: '00:00:00',
      longestWaitTime: '00:00:00',
      serviceLevel: 0,
      abandonedRate: 0,
    },
    channels: {
      whatsapp: 0,
      telegram: 0,
      instagram: 0,
      sms: 0,
    },
    agents: {
      total: 0,
      available: 0,
      busy: 0,
      away: 0,
      offline: 0,
    },
    
  });

  // Função para gerar dados mock de conversas ao longo do tempo
  const generateMockConversationData = (): ConversationData[] => {
    const data: ConversationData[] = [];
    const now = new Date();
    
    // Gerar dados para as últimas 24 horas com variação realista
    for (let i = 23; i >= 0; i--) {
      const time = new Date(now.getTime() - i * 60 * 60 * 1000);
      const hour = time.getHours();
      
      // Simular padrão de conversas: mais conversas durante o dia (9h-18h), menos à noite
      let baseConversations = 15;
      if (hour >= 9 && hour <= 18) {
        baseConversations = 25 + Math.floor(Math.random() * 20); // 25-45 durante o dia
      } else if (hour >= 19 && hour <= 22) {
        baseConversations = 10 + Math.floor(Math.random() * 15); // 10-25 à noite
      } else {
        baseConversations = 5 + Math.floor(Math.random() * 10); // 5-15 madrugada
      }
      
      // Adicionar variação aleatória
      const conversations = baseConversations + Math.floor(Math.random() * 8) - 4;
      
      data.push({
        time: time.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
        conversations: Math.max(0, conversations), // Garantir que não seja negativo
      });
    }
    
    return data;
  };

  // Inicializar estados (valores serão carregados da API ou mock)
  const [conversationData, setConversationData] = useState<ConversationData[]>([]);
  const [conversionData, setConversionData] = useState<ConversationData[]>([]);
  const [channelData, setChannelData] = useState<ChannelData[]>([]);
  const [agentData, setAgentData] = useState<AgentData[]>([]);
  const [recentConversations, setRecentConversations] = useState<RecentConversation[]>([]);
  const [dailyConversations, setDailyConversations] = useState<DailyConversations[]>([]);
  
  // Estados para KPIs (inicializados com 0, serão preenchidos pela API ou mock)
  const [totalLeads, setTotalLeads] = useState(0);
  const [totalConversations, setTotalConversations] = useState(0);
  const [totalDisparos, setTotalDisparos] = useState(0);
  const [leads24h, setLeads24h] = useState(0);
  const [peakHour, setPeakHour] = useState<string>('');
  const [leads3days, setLeads3days] = useState(0);
  const [leadsToday, setLeadsToday] = useState(0);
  const [leadsYesterday, setLeadsYesterday] = useState(0);
  const [conversionRate, setConversionRate] = useState(0);
  const [conversionRate24h, setConversionRate24h] = useState(0);
  const [conversionsCount, setConversionsCount] = useState(0);
  const [leads, setLeads] = useState<any[]>([]);
  const [crmStats, setCrmStats] = useState<any>({});
  const [dashboardStats, setDashboardStats] = useState<any>(null);
  const [previousDayStats, setPreviousDayStats] = useState<any>(null);
  const [agendaEvents, setAgendaEvents] = useState<Event[]>([]);
  const [webhookLogs, setWebhookLogs] = useState<any[]>([]);
  
  // Estados para comparações com dia anterior
  const [comparisons, setComparisons] = useState<{
    conversionRate24h: number | null;
    totalLeads: number | null;
    conversionsCount: number | null;
    leadsToday: number | null;
    conversionRate: number | null;
    leads24h: number | null;
    webhookEvents24h: number | null;
    leadsYesterday: number | null;
  }>({
    conversionRate24h: null,
    totalLeads: null,
    conversionsCount: null,
    leadsToday: null,
    conversionRate: null,
    leads24h: null,
    webhookEvents24h: null,
    leadsYesterday: null,
  });
  
  // Estados para o seletor de período da taxa de conversão
  const [conversionPeriod, setConversionPeriod] = useState<'general' | 'yesterday' | '3days' | '7days' | '1month' | 'custom'>('general');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [datePickerAnchor, setDatePickerAnchor] = useState<HTMLElement | null>(null);
  
  // Estados para o seletor de período dos leads (padrão: 24h)
  const [leadsPeriod, setLeadsPeriod] = useState<'general' | 'yesterday' | '3days' | '7days' | '1month' | 'custom'>('3days'); // 3days representa 24h
  const [leadsCustomStartDate, setLeadsCustomStartDate] = useState<string>('');
  const [leadsCustomEndDate, setLeadsCustomEndDate] = useState<string>('');
  const [leadsDatePickerAnchor, setLeadsDatePickerAnchor] = useState<HTMLElement | null>(null);

  // Cache para otimizar performance (expandido com otimizações)
  const dashboardCache = useRef<{
    allLeads: any[];
    allConversations: any[];
    timestamp: number;
    // Cache de cálculos otimizados
    leadsByPeriod: Map<string, { totalLeads: number; periodLabel: string; filteredLeads: any[] }>;
    conversionByPeriod: Map<string, { conversionRate: number; periodLabel: string; totalLeads: number; convertedLeads: number }>;
    // Cache de conversões processadas
    processedConversions: Map<string, boolean>;
    // Cache de estatísticas do CRM
    crmStatsCache: any;
  }>({
    allLeads: [],
    allConversations: [],
    timestamp: 0,
    leadsByPeriod: new Map(),
    conversionByPeriod: new Map(),
    processedConversions: new Map(),
    crmStatsCache: null
  });
  
  const DASHBOARD_CACHE_DURATION = 5 * 60 * 1000; // 5 minutos de cache (aumentado)
  
  // Estados de loading para UX melhor
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  
  // Hook de otimização do dashboard
  const { filterLeadsByPeriod, calculateConversionRate, cleanupCache } = useDashboardOptimization();

  // Debug: Verificar configuração de faturamento
  React.useEffect(() => {
    console.log('💰 Configuração de Faturamento:', {
      showRevenueCards: config.revenue.showRevenueCards,
      conversionValueBRL: config.revenue.conversionValueBRL,
      env_SHOW_REVENUE_CARDS: process.env.REACT_APP_SHOW_REVENUE_CARDS,
      env_CONVERSION_VALUE_BRL: process.env.REACT_APP_CONVERSION_VALUE_BRL
    });
  }, []);

  // Função para obter ícone do canal
  const getChannelIcon = (channel: string) => {
    switch (channel.toLowerCase()) {
      case 'whatsapp':
        return '💬';
      case 'telegram':
        return '📬';
      case 'instagram':
        return '📷';
      case 'sms':
        return '📱';
      case 'webchat':
        return '🌐';
      case 'email':
        return '📧';
      default:
        return '💬';
    }
  };

  // Função para obter cor do status da conversa
  const getConversationStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return COLORS.success;
      case 'pending':
        return COLORS.warning;
      case 'closed':
        return COLORS.secondary;
      default:
        return COLORS.info;
    }
  };

  // Função para calcular período do dia com mais conversas
  const calculatePeakHour = (conversations: any[]) => {
    const hourCounts: { [key: number]: number } = {};
    
    conversations.forEach((conv: any) => {
      const date = new Date(conv.created_at);
      const hour = date.getHours();
      hourCounts[hour] = (hourCounts[hour] || 0) + 1;
    });
    
    // Encontrar a hora com mais conversas
    let maxCount = 0;
    let peakHour = 0;
    
    for (const [hour, count] of Object.entries(hourCounts)) {
      if (count > maxCount) {
        maxCount = count;
        peakHour = parseInt(hour);
      }
    }
    
    // Formatar período
    if (peakHour >= 6 && peakHour < 12) {
      return `${peakHour}:00 - ${peakHour + 1}:00 (Manhã)`;
    } else if (peakHour >= 12 && peakHour < 18) {
      return `${peakHour}:00 - ${peakHour + 1}:00 (Tarde)`;
    } else if (peakHour >= 18 && peakHour < 22) {
      return `${peakHour}:00 - ${peakHour + 1}:00 (Noite)`;
    } else {
      return `${peakHour}:00 - ${peakHour + 1}:00 (Madrugada)`;
    }
  };

  // Função para formatar tempo relativo
  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return 'agora mesmo';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} min atrás`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hora${hours > 1 ? 's' : ''} atrás`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} dia${days > 1 ? 's' : ''} atrás`;
    }
  };

  // Função para calcular diferença percentual entre dois valores
  const calculatePercentageChange = (current: number, previous: number): number | null => {
    if (previous === 0) {
      // Se o valor anterior era 0, retornar null (sem comparação possível)
      return current > 0 ? 100 : null;
    }
    const change = ((current - previous) / previous) * 100;
    return Math.round(change * 100) / 100; // Arredondar para 2 casas decimais
  };

  // Função para formatar mudança percentual para exibição
  const formatPercentageChange = (change: number | null): { value: string; isPositive: boolean } => {
    if (change === null) {
      return { value: 'N/A', isPositive: false };
    }
    const sign = change >= 0 ? '+' : '';
    return {
      value: `${sign}${change.toFixed(2)}%`,
      isPositive: change >= 0
    };
  };

  // Função otimizada para calcular leads baseada no período selecionado (com cache e useMemo)
  const calculateLeadsByPeriod = useMemo(() => {
    return (period: string, leads: any[], customStart?: string, customEnd?: string, stats?: any) => {
      // Gerar chave de cache
      const cacheKey = `leads_${period}_${customStart || ''}_${customEnd || ''}`;
      
      // Verificar cache
      if (dashboardCache.current.leadsByPeriod.has(cacheKey)) {
        return dashboardCache.current.leadsByPeriod.get(cacheKey)!;
      }

      // Se temos stats pré-calculadas e não temos leads, usar stats
      // OU se temos stats, sempre priorizar stats (mais confiável)
      if (stats) {
        let totalLeads = 0;
        let periodLabel = '';

        switch (period) {
          case 'general':
            periodLabel = 'Geral';
            totalLeads = stats.total_leads || 0;
            break;
          case 'yesterday':
            periodLabel = 'Ontem';
            totalLeads = stats.leads_yesterday || 0;
            break;
          case '3days': // Representa 24h para o card de leads
            periodLabel = 'Últimas 24h';
            totalLeads = stats.leads_24h || 0;
            break;
          case '7days':
            periodLabel = 'Últimos 7 dias';
            totalLeads = stats.leads_7days || 0;
            break;
          case '1month':
            periodLabel = 'Último mês';
            // Não temos leads_30days nas stats, usar total_leads como aproximação
            totalLeads = stats.total_leads || 0;
            break;
          case 'custom':
            if (customStart && customEnd) {
              periodLabel = `${new Date(customStart + 'T00:00:00').toLocaleDateString('pt-BR')} - ${new Date(customEnd + 'T00:00:00').toLocaleDateString('pt-BR')}`;
            } else {
              periodLabel = 'Período personalizado';
            }
            // Para período customizado, usar total_leads como fallback
            totalLeads = stats.total_leads || 0;
            break;
          default:
            periodLabel = 'Últimas 24h';
            totalLeads = stats.leads_24h || 0;
        }

        const result = {
          totalLeads,
          periodLabel,
          filteredLeads: []
        };

        // Salvar no cache
        dashboardCache.current.leadsByPeriod.set(cacheKey, result);
        return result;
      }

      // Se temos leads mas não temos stats, usar a lógica original
      let filteredLeads: any[] = [];
      let periodLabel = '';

      // Processar em lotes para grandes volumes
      const BATCH_SIZE = 1000;
      const processBatch = (batch: any[]) => {
        return batch.filter((lead: any) => {
          const leadDate = new Date(lead.entered_at || lead.contacts?.created_at);
          
          switch (period) {
            case 'general':
              return true;
            case 'yesterday':
              const yesterdayDates = getYesterdayDates();
              return leadDate >= new Date(yesterdayDates.start) && leadDate <= new Date(yesterdayDates.end);
            case '3days': // Representa 24h para o card de leads
              const last24hDates = getLastDaysDates(1);
              return leadDate >= new Date(last24hDates.start) && leadDate <= new Date(last24hDates.end);
            case '7days':
              const last7DaysDates = getLastDaysDates(7);
              return leadDate >= new Date(last7DaysDates.start) && leadDate <= new Date(last7DaysDates.end);
            case '1month':
              const last30DaysDates = getLastDaysDates(30);
              return leadDate >= new Date(last30DaysDates.start) && leadDate <= new Date(last30DaysDates.end);
            case 'custom':
              if (customStart && customEnd) {
                const startDate = new Date(customStart + 'T00:00:00');
                const endDate = new Date(customEnd + 'T23:59:59');
                return leadDate >= startDate && leadDate <= endDate;
              }
              return true;
            default:
              // Padrão: 24h
              const default24hDates = getLastDaysDates(1);
              return leadDate >= new Date(default24hDates.start) && leadDate <= new Date(default24hDates.end);
          }
        });
      };

      // Processar em lotes se necessário
      if (leads.length > BATCH_SIZE) {
        for (let i = 0; i < leads.length; i += BATCH_SIZE) {
          const batch = leads.slice(i, i + BATCH_SIZE);
          filteredLeads.push(...processBatch(batch));
        }
      } else {
        filteredLeads = processBatch(leads);
      }

      // Definir label do período
      switch (period) {
        case 'general':
          periodLabel = 'Geral';
          break;
        case 'yesterday':
          periodLabel = 'Ontem';
          break;
        case '3days':
          periodLabel = 'Últimas 24h';
          break;
        case '7days':
          periodLabel = 'Últimos 7 dias';
          break;
        case '1month':
          periodLabel = 'Último mês';
          break;
        case 'custom':
          if (customStart && customEnd) {
            periodLabel = `${new Date(customStart + 'T00:00:00').toLocaleDateString('pt-BR')} - ${new Date(customEnd + 'T00:00:00').toLocaleDateString('pt-BR')}`;
          } else {
            periodLabel = 'Período personalizado';
          }
          break;
        default:
          periodLabel = 'Últimas 24h';
      }

      const result = {
        totalLeads: filteredLeads.length,
        periodLabel,
        filteredLeads
      };

      // Armazenar no cache
      dashboardCache.current.leadsByPeriod.set(cacheKey, result);
      
      return result;
    };
  }, []);

  // Função otimizada para calcular taxa de conversão baseada no período selecionado (com cache e useMemo)
  const calculateConversionRateByPeriod = useMemo(() => {
    return (period: string, leads: any[], customStart?: string, customEnd?: string, stats?: any) => {
      // Gerar chave de cache
      const cacheKey = `conversion_${period}_${customStart || ''}_${customEnd || ''}`;
      
      // Verificar cache
      if (dashboardCache.current.conversionByPeriod.has(cacheKey)) {
        return dashboardCache.current.conversionByPeriod.get(cacheKey)!;
      }

      // Se temos stats pré-calculadas e não temos leads, usar stats
      if (stats && (!leads || leads.length === 0)) {
        let conversionRate = 0;
        let convertedLeads = 0;
        let totalLeads = 0;
        let periodLabel = '';

        switch (period) {
          case 'general':
            periodLabel = 'Geral';
            // Usar diretamente a taxa de conversão pré-calculada das stats
            conversionRate = stats.conversion_rate || 0;
            convertedLeads = stats.converted_leads || 0;
            totalLeads = stats.total_leads || 0;
            break;
          case 'yesterday':
            periodLabel = 'Ontem';
            // Para ontem, calcular baseado nos leads de ontem
            totalLeads = stats.leads_yesterday || 0;
            // Não temos converted_leads_yesterday nas stats, então estimar baseado na taxa geral
            convertedLeads = totalLeads > 0 ? Math.round((totalLeads * (stats.conversion_rate || 0)) / 100) : 0;
            conversionRate = totalLeads > 0 ? ((convertedLeads / totalLeads) * 100) : 0;
            break;
          case '3days':
            periodLabel = 'Últimos 3 dias';
            totalLeads = stats.leads_3days || 0;
            convertedLeads = totalLeads > 0 ? Math.round((totalLeads * (stats.conversion_rate || 0)) / 100) : 0;
            conversionRate = totalLeads > 0 ? ((convertedLeads / totalLeads) * 100) : 0;
            break;
          case '7days':
            periodLabel = 'Últimos 7 dias';
            totalLeads = stats.leads_7days || 0;
            convertedLeads = totalLeads > 0 ? Math.round((totalLeads * (stats.conversion_rate || 0)) / 100) : 0;
            conversionRate = totalLeads > 0 ? ((convertedLeads / totalLeads) * 100) : 0;
            break;
          case '1month':
            periodLabel = 'Último mês';
            // Não temos leads_30days nas stats, usar total_leads como aproximação
            totalLeads = stats.total_leads || 0;
            convertedLeads = stats.converted_leads || 0;
            conversionRate = stats.conversion_rate || 0;
            break;
          case 'custom':
            if (customStart && customEnd) {
              periodLabel = `${new Date(customStart + 'T00:00:00').toLocaleDateString('pt-BR')} - ${new Date(customEnd + 'T00:00:00').toLocaleDateString('pt-BR')}`;
            } else {
              periodLabel = 'Período personalizado';
            }
            // Para período customizado, usar taxa geral como fallback
            conversionRate = stats.conversion_rate || 0;
            convertedLeads = stats.converted_leads || 0;
            totalLeads = stats.total_leads || 0;
            break;
          default:
            periodLabel = 'Geral';
            conversionRate = stats.conversion_rate || 0;
            convertedLeads = stats.converted_leads || 0;
            totalLeads = stats.total_leads || 0;
        }

        const result = {
          conversionRate: Math.round(conversionRate * 100) / 100,
          convertedLeads,
          totalLeads,
          periodLabel
        };

        // Salvar no cache
        dashboardCache.current.conversionByPeriod.set(cacheKey, result);
        return result;
      }

      // Se temos leads, usar a lógica original
      let filteredLeads: any[] = [];
      let periodLabel = '';

      // Processar em lotes para grandes volumes
      const BATCH_SIZE = 1000;
      const processBatch = (batch: any[]) => {
        return batch.filter((lead: any) => {
          const leadDate = new Date(lead.entered_at || lead.contacts?.created_at);
          
          switch (period) {
            case 'general':
              return true;
            case 'yesterday':
              const yesterdayDates = getYesterdayDates();
              return leadDate >= new Date(yesterdayDates.start) && leadDate <= new Date(yesterdayDates.end);
            case '3days':
              const last3DaysDates = getLastDaysDates(3);
              return leadDate >= new Date(last3DaysDates.start) && leadDate <= new Date(last3DaysDates.end);
            case '7days':
              const last7DaysDates = getLastDaysDates(7);
              return leadDate >= new Date(last7DaysDates.start) && leadDate <= new Date(last7DaysDates.end);
            case '1month':
              const last30DaysDates = getLastDaysDates(30);
              return leadDate >= new Date(last30DaysDates.start) && leadDate <= new Date(last30DaysDates.end);
            case 'custom':
              if (customStart && customEnd) {
                const startDate = new Date(customStart + 'T00:00:00');
                const endDate = new Date(customEnd + 'T23:59:59');
                return leadDate >= startDate && leadDate <= endDate;
              }
              return true;
            default:
              return true;
          }
        });
      };

      // Processar em lotes se necessário
      if (leads.length > BATCH_SIZE) {
        for (let i = 0; i < leads.length; i += BATCH_SIZE) {
          const batch = leads.slice(i, i + BATCH_SIZE);
          filteredLeads.push(...processBatch(batch));
        }
      } else {
        filteredLeads = processBatch(leads);
      }

      // Definir label do período
      switch (period) {
        case 'general':
          periodLabel = 'Geral';
          break;
        case 'yesterday':
          periodLabel = 'Ontem';
          break;
        case '3days':
          periodLabel = 'Últimos 3 dias';
          break;
        case '7days':
          periodLabel = 'Últimos 7 dias';
          break;
        case '1month':
          periodLabel = 'Último mês';
          break;
        case 'custom':
          if (customStart && customEnd) {
            periodLabel = `${new Date(customStart + 'T00:00:00').toLocaleDateString('pt-BR')} - ${new Date(customEnd + 'T00:00:00').toLocaleDateString('pt-BR')}`;
          } else {
            periodLabel = 'Período personalizado';
          }
          break;
        default:
          periodLabel = 'Geral';
      }

      // Calcular conversões de forma otimizada
      const conversions = filteredLeads.filter((lead: any) => {
        return lead.crm_stages && lead.crm_stages.stage_order === 4;
      }).length;

      const rate = filteredLeads.length > 0 ? (conversions / filteredLeads.length) * 100 : 0;
      
      const result = {
        conversionRate: Math.round(rate * 100) / 100,
        convertedLeads: conversions,
        totalLeads: filteredLeads.length,
        periodLabel
      };

      // Armazenar no cache
      dashboardCache.current.conversionByPeriod.set(cacheKey, result);
      
      return result;
    };
  }, []);

  // Função para obter os nomes reais dos estágios do backend
  const getStageNames = () => {
    if (crmStats && crmStats.stageStats) {
      const names = crmStats.stageStats.map((stage: any) => stage.name).filter(Boolean);
      console.log('🎯 Nomes dos estágios:', names);
      return names;
    }
    // Fallback para nomes padrão
    console.log('⚠️ Usando nomes padrão dos estágios');
    return ['Novo Lead', 'Grupo', 'Cadastro', 'Ganho'];
  };

  // Função para mapear estágios do CRM usando dados reais do backend
  const getLeadsByStage = (stageName: string) => {
    // Dados mock para cada estágio (números maiores e diferentes)
    const mockStageCounts: Record<string, number> = {
      'novo lead': 142,
      'grupo': 89,
      'cadastro': 203,
      'ganho': 156
    };
    
    // Sempre retornar dados mock
    const count = mockStageCounts[stageName.toLowerCase()] || 0;
    console.log(`🎨 Estágio "${stageName}": ${count} leads (mock)`);
    return count;
    
    // Código comentado - usar dados reais se necessário no futuro
    /*
    // Se temos dados de estágios do backend (stageCounts), usar eles
    if (crmStats && crmStats.stageCounts) {
      // Mapear nome do estágio para stage_order
      const stageOrderMap: Record<string, number> = {
        'novo lead': 1,
        'grupo': 2,
        'cadastro': 3,
        'ganho': 4
      };
      
      const stageOrder = stageOrderMap[stageName.toLowerCase()];
      if (stageOrder && crmStats.stageCounts[stageOrder] !== undefined) {
        const count = crmStats.stageCounts[stageOrder] || 0;
        console.log(`📊 Estágio "${stageName}" (order ${stageOrder}): ${count} leads`);
        return count;
      }
    }
    
    // Fallback: tentar usar stageStats se disponível
    if (crmStats && crmStats.stageStats) {
      const stageData = crmStats.stageStats.find((stage: any) => 
        stage.name?.toLowerCase() === stageName.toLowerCase()
      );
      const count = stageData ? (stageData.count || 0) : 0;
      console.log(`📊 Estágio "${stageName}": ${count} leads (via stageStats)`);
      return count;
    }
    
    // Fallback para o método anterior se não temos dados do backend
    if (!leads || leads.length === 0) return 0;
    
    return leads.filter((lead: any) => {
      // Verificar diferentes campos possíveis
      const stage = lead.stage || lead.status || lead.stage_name || lead.stageName || lead.estagio || lead.estado;
      const stageLower = stage ? stage.toLowerCase() : '';
      
      // Mapear estágios para diferentes formatos possíveis
      switch (stageName.toLowerCase()) {
        case 'novo lead':
          return stageLower.includes('novo') || stageLower.includes('new') || stageLower.includes('lead');
        case 'grupo':
          return stageLower.includes('grupo') || stageLower.includes('group');
        case 'cadastro':
          return stageLower.includes('cadastro') || stageLower.includes('register') || stageLower.includes('form');
        case 'ganho':
          return stageLower.includes('ganho') || stageLower.includes('won') || stageLower.includes('converted');
        default:
          return false;
      }
    }).length;
    */
  };

  // Cores para gráficos
  const COLORS = {
    primary: '#1976d2',
    secondary: '#dc004e',
    success: '#4caf50',
    warning: '#ff9800',
    error: '#f44336',
    info: '#2196f3',
    whatsapp: '#25d366',
    telegram: '#0088cc',
    instagram: '#e4405f',
    sms: '#ff6b35',
  };


  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Buscar dados da API
      console.log('🚀 Dashboard: Buscando dados otimizados da API...');

      // Criar função helper para timeout
      const createTimeoutPromise = (timeout: number) => new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Timeout')), timeout)
      );

      // Buscar estatísticas pré-calculadas (muito mais rápido) com timeouts
      const [
        dashboardStatsResponse,
        campaignsResponse,
        recentConversationsResponse,
        cacheDataResponse,
        previousDayStatsResponse,
        agendaEventsResponse,
        webhookLogsResponse,
        crmStatsResponse,
      ] = await Promise.allSettled([
        // Stats principais - crítico, timeout de 5 segundos
        Promise.race([
          axios.get(getApiUrl('/dashboard-cache/dashboard-stats'), { timeout: 5000 }),
          createTimeoutPromise(5000)
        ]).catch(() => ({ data: { data: {} } })),
        // Campanhas - não crítico, timeout de 3 segundos
        Promise.race([
          axios.get(getApiUrl('/disparos?limit=10000&page=1'), { timeout: 3000 }),
          createTimeoutPromise(3000)
        ]).catch(() => ({ data: { data: [] } })),
        // Conversas recentes - não crítico, timeout de 2 segundos
        Promise.race([
          axios.get(getApiUrl('/conversations?limit=5&page=1&sortBy=updated_at&sortOrder=desc'), { timeout: 2000 }),
          createTimeoutPromise(2000)
        ]).catch(() => ({ data: { data: [] } })),
        // Cache data - não crítico, timeout de 3 segundos
        Promise.race([
          axios.get(getApiUrl('/dashboard-cache/data'), { timeout: 3000 }),
          createTimeoutPromise(3000)
        ]).catch(() => ({ data: { data: {} } })),
        // Buscar stats do dia anterior para comparações
        (async () => {
          try {
            const yesterday = new Date();
            yesterday.setDate(yesterday.getDate() - 1);
            yesterday.setHours(0, 0, 0, 0);
            const yesterdayStr = yesterday.toISOString().split('T')[0];
            // Tentar buscar stats do dia anterior via endpoint ou calcular
            // Por enquanto, vamos usar os dados disponíveis nas stats de hoje
            return null; // Será calculado abaixo
          } catch (err) {
            return null;
          }
        })(),
        // Buscar eventos da agenda (próximos 2 dias) com timeout curto de 2 segundos
        (async () => {
          try {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const tomorrow = new Date(today);
            tomorrow.setDate(tomorrow.getDate() + 2);
            
            const result = await Promise.race([
              agendaService.getEvents({
                start_date: today.toISOString().split('T')[0],
                end_date: tomorrow.toISOString().split('T')[0],
              }),
              createTimeoutPromise(2000)
            ]) as any;
            
            return result.success ? result.data : [];
          } catch (err) {
            // Silenciosamente retornar array vazio se falhar ou timeout
            return [];
          }
        })(),
        // Buscar webhook logs (últimos 4 eventos de integrações) com timeout curto
        (async () => {
          try {
            // Buscar de múltiplas fontes e consolidar
            // Nota: Hotmart não possui endpoint de logs de webhook, apenas Hubla e Kirvano
            const [hublaLogs, kirvanoLogs] = await Promise.allSettled([
              Promise.race([
                axios.get(getApiUrl('/hubla/webhook/logs?limit=4'), { timeout: 2000 }),
                createTimeoutPromise(2000)
              ]).catch(() => ({ data: { data: [] } })),
              Promise.race([
                axios.get(getApiUrl('/kirvano/webhook/logs?limit=4'), { timeout: 2000 }),
                createTimeoutPromise(2000)
              ]).catch(() => ({ data: { data: [] } })),
            ]);
            
            // Consolidar logs de todas as fontes
            const allLogs: any[] = [];
            
            if (hublaLogs.status === 'fulfilled') {
              const hublaValue = hublaLogs.value as any;
              if (hublaValue?.data?.data) {
                allLogs.push(...hublaValue.data.data.map((log: any) => ({ ...log, source: 'hubla' })));
              }
            }
            if (kirvanoLogs.status === 'fulfilled') {
              const kirvanoValue = kirvanoLogs.value as any;
              if (kirvanoValue?.data?.data) {
                allLogs.push(...kirvanoValue.data.data.map((log: any) => ({ ...log, source: 'kirvano' })));
              }
            }
            
            // Ordenar por data de processamento (mais recente primeiro) e limitar a 4
            return allLogs
              .sort((a, b) => {
                const dateA = new Date(a.processed_at || a.created_at || 0);
                const dateB = new Date(b.processed_at || b.created_at || 0);
                return dateB.getTime() - dateA.getTime();
              })
              .slice(0, 4);
          } catch (err) {
            console.warn('Erro ao buscar webhook logs:', err);
            return [];
          }
        })(),
        // Buscar estatísticas do CRM com dados de estágios - não crítico, timeout de 2 segundos
        Promise.race([
          axios.get(getApiUrl('/crm/stats'), { timeout: 2000 }),
          createTimeoutPromise(2000)
        ]).catch(() => ({ data: { success: false, data: {} } })),
      ]);

      // Processar stats pré-calculadas
      const stats = dashboardStatsResponse.status === 'fulfilled'
        ? (dashboardStatsResponse.value as any)?.data?.data || {}
        : {};

      console.log('📊 Dashboard Stats recebidas:', stats);
      
      // Armazenar stats para uso no render
      setDashboardStats(stats);

      // Buscar stats do dia anterior para comparações
      // Como o backend salva stats diárias, vamos tentar buscar a stats de ontem
      // Se não conseguir, vamos usar aproximações baseadas nos dados disponíveis
      let previousStats: any = null;
      try {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        yesterday.setHours(0, 0, 0, 0);
        const yesterdayStr = yesterday.toISOString().split('T')[0];
        
        // Tentar buscar stats do dia anterior diretamente do banco via endpoint
        // Por enquanto, vamos usar os dados disponíveis nas stats de hoje
        // O backend já fornece leads_yesterday, então podemos fazer algumas comparações
        previousStats = null; // Será calculado abaixo usando dados disponíveis
      } catch (err) {
        console.warn('⚠️ Não foi possível buscar stats do dia anterior:', err);
      }

      setPreviousDayStats(previousStats);

      // Processar eventos da agenda
      const events = agendaEventsResponse.status === 'fulfilled' && Array.isArray(agendaEventsResponse.value)
        ? agendaEventsResponse.value
        : [];
      
      // Ordenar eventos por data e hora
      const sortedEvents = events
        .filter((event: Event) => {
          const eventDate = new Date(event.event_date + 'T' + (event.start_time || '00:00:00'));
          const now = new Date();
          // Incluir apenas eventos futuros ou de hoje
          return eventDate >= new Date(now.setHours(0, 0, 0, 0));
        })
        .sort((a: Event, b: Event) => {
          const dateA = new Date(a.event_date + 'T' + (a.start_time || '00:00:00'));
          const dateB = new Date(b.event_date + 'T' + (b.start_time || '00:00:00'));
          return dateA.getTime() - dateB.getTime();
        })
        .slice(0, 5); // Limitar a 5 eventos
      
      setAgendaEvents(sortedEvents);

      // Processar webhook logs
      const logs = webhookLogsResponse.status === 'fulfilled' && Array.isArray(webhookLogsResponse.value)
        ? webhookLogsResponse.value
        : [];
      
      setWebhookLogs(logs);

      // Processar dados de campanhas
      const campaigns = campaignsResponse.status === 'fulfilled'
        ? (campaignsResponse.value as any)?.data?.data || []
        : [];

      // Processar dados de conversas recentes
      const recentConversationsData = recentConversationsResponse.status === 'fulfilled'
        ? (recentConversationsResponse.value as any)?.data?.data || []
        : [];

      // Buscar conversas para cálculos de peak hour e métricas de performance
      // (apenas uma amostra para não sobrecarregar)
      let conversations: any[] = [];
      try {
        const conversationsResponse = await axios.get(getApiUrl('/conversations?limit=1000&page=1&sortBy=created_at&sortOrder=desc'));
        conversations = conversationsResponse.data.data || [];
      } catch (error) {
        console.warn('Erro ao buscar conversas para análises:', error);
      }

      // Calcular datas para filtros (para peak hour)
      const last24hDates = getLastDaysDates(1);
      const conversations24h = conversations.filter((conv: any) => {
        const convDate = new Date(conv.created_at);
        return convDate >= new Date(last24hDates.start) && convDate <= new Date(last24hDates.end);
      });
      const peakHourPeriod = calculatePeakHour(conversations24h);

      // Usar valores das stats pré-calculadas (dados reais)
      const calculatedTotalLeads = stats.total_leads || 0;
      const calculatedLeadsToday = stats.leads_today || 0;
      const calculatedLeadsYesterday = stats.leads_yesterday || 0;
      const calculatedLeads24h = stats.leads_24h || 0;
      const calculatedLeads3days = stats.leads_3days || 0;
      const calculatedConversionRate = stats.conversion_rate || 0;
      const calculatedConversionRate24h = stats.conversion_rate_24h || 0;
      const calculatedConversionsCount = stats.converted_leads || 0;
      const calculatedTotalConversations = stats.total_conversations || 0;
      const calculatedTotalDisparos = stats.total_disparos || campaigns.length || 0;
      
      // Atualizar estados com dados reais da API
      setTotalLeads(calculatedTotalLeads);
      setTotalConversations(calculatedTotalConversations);
      setTotalDisparos(calculatedTotalDisparos);
      setLeads24h(calculatedLeads24h);
      setLeads3days(calculatedLeads3days);
      setPeakHour(peakHourPeriod);
      setConversionRate24h(calculatedConversionRate24h);
      setLeadsToday(calculatedLeadsToday);
      setLeadsYesterday(calculatedLeadsYesterday);
      setConversionRate(calculatedConversionRate);
      setConversionsCount(calculatedConversionsCount);
      setLeads([]); // Não precisamos mais dos leads completos
      
      // Calcular comparações com dia anterior
      // Usar os dados disponíveis nas stats de hoje para fazer comparações inteligentes
      // O backend já fornece leads_yesterday, então podemos calcular algumas comparações
      
      // Para métricas que não temos dados históricos, vamos usar aproximações:
      // - Se temos leads_yesterday, podemos estimar outras métricas baseadas nisso
      // - Para taxa de conversão, podemos usar a taxa atual como referência
      
      // Estimar total de leads de ontem (aproximação: total de hoje menos leads de hoje)
      const estimatedYesterdayTotalLeads = Math.max(0, calculatedTotalLeads - calculatedLeadsToday);
      
      // Estimar conversões de ontem (aproximação: usar taxa de conversão geral aplicada aos leads de ontem)
      const estimatedYesterdayConversions = calculatedLeadsYesterday > 0 && calculatedConversionRate > 0
        ? Math.round((calculatedLeadsYesterday * calculatedConversionRate) / 100)
        : 0;
      
      // Estimar taxa de conversão de ontem (aproximação: usar taxa geral se não tiver específica)
      // Se temos leads de ontem e conversões estimadas, calcular taxa; senão usar taxa geral
      const estimatedYesterdayConversionRate = calculatedLeadsYesterday > 0 && estimatedYesterdayConversions > 0
        ? (estimatedYesterdayConversions / calculatedLeadsYesterday) * 100
        : calculatedConversionRate;
      
      // Estimar taxa de conversão 24h de ontem (aproximação: usar taxa geral de ontem)
      const estimatedYesterdayConversionRate24h = estimatedYesterdayConversionRate;
      
      // Calcular comparações
      const calculatedComparisons = {
        // Taxa de conversão 24h: comparar com taxa de conversão 24h estimada de ontem
        conversionRate24h: calculatedConversionRate24h > 0 && estimatedYesterdayConversionRate24h > 0
          ? calculatePercentageChange(calculatedConversionRate24h, estimatedYesterdayConversionRate24h)
          : null,
        // Total de Leads: comparar com total estimado de ontem
        totalLeads: estimatedYesterdayTotalLeads > 0
          ? calculatePercentageChange(calculatedTotalLeads, estimatedYesterdayTotalLeads)
          : null,
        // Conversões: comparar com conversões estimadas de ontem
        conversionsCount: estimatedYesterdayConversions > 0
          ? calculatePercentageChange(calculatedConversionsCount, estimatedYesterdayConversions)
          : null,
        // Leads Hoje: comparar com leads de ontem (já temos leadsYesterday)
        leadsToday: calculatedLeadsYesterday > 0
          ? calculatePercentageChange(calculatedLeadsToday, calculatedLeadsYesterday)
          : null,
        // Taxa de conversão geral: comparar com taxa estimada de ontem
        conversionRate: estimatedYesterdayConversionRate > 0
          ? calculatePercentageChange(calculatedConversionRate, estimatedYesterdayConversionRate)
          : null,
        // Leads 24h: comparar com leads de ontem (aproximação)
        leads24h: calculatedLeadsYesterday > 0
          ? calculatePercentageChange(calculatedLeads24h, calculatedLeadsYesterday)
          : null,
        // Eventos 24h: não temos dados históricos, deixar null por enquanto
        webhookEvents24h: null,
        // Leads Ontem: não temos dados de 2 dias atrás, deixar null
        leadsYesterday: null,
      };
      
      setComparisons(calculatedComparisons);
      
      // Processar estatísticas do CRM
      const crmStatsValue = crmStatsResponse.status === 'fulfilled' ? (crmStatsResponse.value as any) : null;
      const crmStatsData = crmStatsValue?.data?.success
        ? crmStatsValue.data.data || {}
        : {};
      
      // Atualizar crmStats com dados reais
      setCrmStats({
        totalLeads: calculatedTotalLeads,
        convertedLeads: calculatedConversionsCount,
        stageCounts: {
          1: stats.stage_1_count || 0,
          2: stats.stage_2_count || 0,
          3: stats.stage_3_count || 0,
          4: stats.stage_4_count || 0,
        },
        // Adicionar stageStats se disponível
        stageStats: crmStatsData.stageStats || []
      });
      
      // Calcular métricas de conversas usando stats pré-calculadas (sem dados mock)
      const incoming = calculatedTotalConversations;
      const inQueue = stats.conversations_pending || 0;
      const ongoing = stats.conversations_active || 0;
      const completed = stats.conversations_completed || 0;
      const abandoned = stats.conversations_abandoned || 0;

      // Calcular métricas de performance reais (sem dados mock)
      const avgSpeedToAnswer = conversations.length > 0 ? formatTime(calculateAverageResponseTime(conversations)) : '00:00:00';
      const avgHandleTime = conversations.length > 0 ? formatTime(calculateAverageHandleTime(conversations)) : '00:00:00';
      const longestWaitTime = conversations.length > 0 ? formatTime(calculateLongestWaitTime(conversations)) : '00:00:00';
      const serviceLevel = conversations.length > 0 ? calculateServiceLevel(conversations) : 0;
      const abandonedRate = conversations.length > 0 ? calculateAbandonedRate(conversations) : 0;

      // Distribuir conversas por canal usando stats pré-calculadas ou amostra
      const whatsapp = stats.conversations_whatsapp || conversations.filter((conv: any) => conv.channel === 'whatsapp').length;
      const telegram = stats.conversations_telegram || conversations.filter((conv: any) => conv.channel === 'telegram').length;
      const instagram = stats.conversations_instagram || conversations.filter((conv: any) => conv.channel === 'instagram').length;
      const sms = stats.conversations_sms || conversations.filter((conv: any) => conv.channel === 'sms').length;

      // Dados de agentes: tentar buscar da API, senão usar valores padrão
      let totalAgents = 8;
      let available = Math.floor(totalAgents * 0.4);
      let busy = Math.floor(totalAgents * 0.25);
      let away = Math.floor(totalAgents * 0.2);
      let offline = totalAgents - available - busy - away;
      
      // Se stats tiver dados de agentes, usar eles
      if (stats.agents_total) {
        totalAgents = stats.agents_total || 8;
        available = stats.agents_available || Math.floor(totalAgents * 0.4);
        busy = stats.agents_busy || Math.floor(totalAgents * 0.25);
        away = stats.agents_away || Math.floor(totalAgents * 0.2);
        offline = totalAgents - available - busy - away;
      }

      setMetrics({
        conversations: {
          incoming,
          inQueue,
          ongoing,
          completed,
          abandoned,
        },
        performance: {
          avgSpeedToAnswer,
          avgHandleTime,
          longestWaitTime,
          serviceLevel,
          abandonedRate,
        },
        channels: {
          whatsapp,
          telegram,
          instagram,
          sms,
        },
        agents: {
          total: totalAgents,
          available,
          busy,
          away,
          offline,
        },

      });

      // Gerar dados de conversas ao longo do tempo
      // Primeiro tenta usar dados pré-calculados do cache, senão gera a partir das conversas
      let timeData: ConversationData[] = [];
      
      // Tentar usar dados do cache (conversationTimeData)
      const cacheData = cacheDataResponse.status === 'fulfilled' 
        ? (cacheDataResponse.value as any)?.data?.data 
        : null;
      
      if (cacheData?.conversationTimeData && Array.isArray(cacheData.conversationTimeData) && cacheData.conversationTimeData.length > 0) {
        // Usar dados pré-calculados do cache (dados reais)
        timeData = cacheData.conversationTimeData.map((item: any) => ({
          time: item.time || '',
          conversations: item.conversations || 0
        }));
        console.log('📊 Usando dados de conversas por hora do cache:', timeData.length, 'horas');
        setConversationData(timeData);
      } else if (conversations.length > 0) {
        // Gerar a partir das conversas reais
        timeData = generateTimeData(conversations);
        console.log('📊 Gerando dados de conversas a partir de dados reais:', timeData.length, 'horas');
        setConversationData(timeData);
      } else {
        // Se não houver dados reais, manter array vazio
        console.log('📊 Nenhum dado real disponível');
        setConversationData([]);
      }

      // Gerar dados de conversões ao longo do tempo (vazio, pois não carregamos mais todos os leads)
      // Isso pode ser melhorado no futuro com stats pré-calculadas por hora
      setConversionData([]);

      // Gerar dados por canal
      const channelChartData = [
        { channel: 'WhatsApp', open: whatsapp * 0.5, active: whatsapp * 0.3, wrapup: whatsapp * 0.2 },
        { channel: 'Telegram', open: telegram * 0.4, active: telegram * 0.4, wrapup: telegram * 0.2 },
        { channel: 'Instagram', open: instagram * 0.6, active: instagram * 0.2, wrapup: instagram * 0.2 },
        { channel: 'SMS', open: sms * 0.3, active: sms * 0.5, wrapup: sms * 0.2 },
      ];
      setChannelData(channelChartData);

      // Gerar dados de agentes: usar dados mock (endpoint /agents não existe)
      // O endpoint de agents não está implementado no backend, então sempre usamos dados mock
      const agents: AgentData[] = [
        { name: 'Ana Silva', status: 'available' as const, conversations: 12, avgResponseTime: '00:02:15' },
        { name: 'Carlos Santos', status: 'busy' as const, conversations: 8, avgResponseTime: '00:03:45' },
        { name: 'Maria Costa', status: 'available' as const, conversations: 15, avgResponseTime: '00:01:55' },
        { name: 'João Oliveira', status: 'away' as const, conversations: 5, avgResponseTime: '00:04:20' },
        { name: 'Lucia Ferreira', status: 'busy' as const, conversations: 10, avgResponseTime: '00:02:30' },
      ];
      setAgentData(agents);

        // Processar dados reais de conversas recentes
        const processedRecentConversations = recentConversationsData.map((conv: any) => ({
          id: conv.id,
          contactName: conv.contact?.name || 'Contato sem nome',
          channel: conv.channel?.type || 'unknown',
          status: conv.status || 'pending',
          lastMessage: conv.last_message || 'Sem mensagens',
          lastMessageTime: formatTimeAgo(conv.last_message_at || conv.updated_at),
          agentName: conv.assigned_agent?.name || undefined
        }));
                setRecentConversations(processedRecentConversations);

        // Calcular conversas por dia dos últimos 7 dias (usando amostra)
        const last7Days = [];
        for (let i = 6; i >= 0; i--) {
          const date = new Date();
          date.setDate(date.getDate() - i);
          last7Days.push(date.toISOString().split('T')[0]);
        }

        const conversationsByDay = last7Days.map(date => {
          const dayConversations = conversations.filter((conv: any) => {
            const convDate = new Date(conv.created_at).toISOString().split('T')[0];
            return convDate === date;
          });
          
          return {
            date: new Date(date).toLocaleDateString('pt-BR', { 
              weekday: 'short', 
              month: 'short', 
              day: 'numeric' 
            }),
            conversations: dayConversations.length
          };
        });

        setDailyConversations(conversationsByDay);

        setLastUpdate(new Date());
    } catch (err) {
      console.error('Erro ao buscar dados do dashboard:', err);
      setError('Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Auto-update a cada 5 minutos (reduzido para melhor performance)
  useEffect(() => {
    if (!autoUpdate) return;

    const interval = setInterval(() => {
      fetchDashboardData();
    }, 5 * 60 * 1000); // 5 minutos

    return () => clearInterval(interval);
  }, [autoUpdate, fetchDashboardData]);


  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Funções para calcular métricas reais
  const calculateAverageResponseTime = (conversations: any[]): number => {
    if (conversations.length === 0) return 0;
    const responseTimes = conversations
      .filter((conv: any) => conv.response_time)
      .map((conv: any) => conv.response_time);
    return responseTimes.length > 0 ? responseTimes.reduce((a: number, b: number) => a + b, 0) / responseTimes.length : 300; // 5 min padrão
  };

  const calculateAverageHandleTime = (conversations: any[]): number => {
    if (conversations.length === 0) return 0;
    const handleTimes = conversations
      .filter((conv: any) => conv.handle_time)
      .map((conv: any) => conv.handle_time);
    return handleTimes.length > 0 ? handleTimes.reduce((a: number, b: number) => a + b, 0) / handleTimes.length : 1200; // 20 min padrão
  };

  const calculateLongestWaitTime = (conversations: any[]): number => {
    if (conversations.length === 0) return 0;
    const waitTimes = conversations
      .filter((conv: any) => conv.wait_time)
      .map((conv: any) => conv.wait_time);
    return waitTimes.length > 0 ? Math.max(...waitTimes) : 1800; // 30 min padrão
  };

  const calculateServiceLevel = (conversations: any[]): number => {
    if (conversations.length === 0) return 0;
    const answeredWithin60s = conversations.filter((conv: any) => conv.response_time && conv.response_time <= 60).length;
    return Math.round((answeredWithin60s / conversations.length) * 100);
  };

  const calculateAbandonedRate = (conversations: any[]): number => {
    if (conversations.length === 0) return 0;
    const abandoned = conversations.filter((conv: any) => conv.status === 'abandoned').length;
    return Math.round((abandoned / conversations.length) * 100);
  };

  const generateTimeData = (conversations: any[]): ConversationData[] => {
    const data = [];
    const now = new Date();
    
    // Agrupar conversas por hora nas últimas 24 horas
    for (let i = 23; i >= 0; i--) {
      const time = new Date(now.getTime() - i * 60 * 60 * 1000);
      const hourStart = new Date(time.getTime());
      const hourEnd = new Date(time.getTime() + 60 * 60 * 1000);
      
      // Contar conversas criadas nesta hora
      const conversationsInHour = conversations.filter((conv: any) => {
        // Tentar diferentes formatos de data que podem vir da API
        let convDate: Date;
        if (conv.created_at) {
          convDate = new Date(conv.created_at);
        } else if (conv.createdAt) {
          convDate = new Date(conv.createdAt);
        } else if (conv.timestamp) {
          convDate = new Date(conv.timestamp);
        } else if (conv.date) {
          convDate = new Date(conv.date);
        } else {
          // Se não houver data, usar data atual (conversa recente)
          convDate = new Date();
        }
        
        // Verificar se a data é válida
        if (isNaN(convDate.getTime())) {
          return false;
        }
        
        return convDate >= hourStart && convDate < hourEnd;
      }).length;
      
      data.push({
        time: time.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
        conversations: conversationsInHour,
      });
    }
    return data;
  };

  const generateConversionTimeData = (leads: any[]): ConversationData[] => {
    const data = [];
    const now = new Date();
    
    // Agrupar conversões por hora nas últimas 24 horas
    for (let i = 23; i >= 0; i--) {
      const time = new Date(now.getTime() - i * 60 * 60 * 1000);
      const hourStart = new Date(time.getTime());
      const hourEnd = new Date(time.getTime() + 60 * 60 * 1000);
      
      // Contar conversões (leads que entraram no estágio 4) nesta hora
      const conversionsInHour = leads.filter((lead: any) => {
        // Verificar se é uma conversão (estágio final)
        const isConversion = lead.crm_stages && lead.crm_stages.stage_order === 4;
        if (!isConversion) return false;
        
        // Debug: Log da estrutura do lead
        console.log('🔍 Lead conversion debug:', {
          leadId: lead.id || lead.contacts?.id,
          leadName: lead.contacts?.name,
          stageOrder: lead.crm_stages?.stage_order,
          hasPipelineStages: !!lead.contact_pipeline_stages,
          pipelineStagesData: lead.contact_pipeline_stages,
          created_at: lead.created_at,
          updated_at: lead.updated_at,
          contacts_created_at: lead.contacts?.created_at,
          contacts_updated_at: lead.contacts?.updated_at
        });
        
        // Usar a data de entrada no estágio 4 (contact_pipeline_stages.entered_at)
        // Se não houver, usar a data de atualização do lead
        let conversionDate: Date;
        
        if (lead.contact_pipeline_stages && lead.contact_pipeline_stages.entered_at) {
          // Data quando o lead entrou no estágio 4
          conversionDate = new Date(lead.contact_pipeline_stages.entered_at);
          console.log('📅 Using pipeline stage entered_at:', lead.contact_pipeline_stages.entered_at, '->', conversionDate);
        } else if (lead.updated_at) {
          // Data de última atualização (fallback)
          conversionDate = new Date(lead.updated_at);
          console.log('📅 Using lead updated_at:', lead.updated_at, '->', conversionDate);
        } else if (lead.created_at) {
          // Data de criação (fallback)
          conversionDate = new Date(lead.created_at);
          console.log('📅 Using lead created_at:', lead.created_at, '->', conversionDate);
        } else if (lead.contacts?.created_at) {
          // Data de criação do contato (fallback)
          conversionDate = new Date(lead.contacts.created_at);
          console.log('📅 Using contact created_at:', lead.contacts.created_at, '->', conversionDate);
        } else {
          // Se não houver data, usar data atual (lead recente)
          conversionDate = new Date();
          console.log('📅 No date found, using current time:', conversionDate);
        }
        
        // Verificar se a data é válida
        if (isNaN(conversionDate.getTime())) {
          console.log('❌ Invalid date for lead:', lead.id || lead.contacts?.id);
          return false;
        }
        
        const isInHour = conversionDate >= hourStart && conversionDate < hourEnd;
        console.log('⏰ Hour check:', {
          hour: time.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
          conversionDate: conversionDate.toLocaleString('pt-BR'),
          hourStart: hourStart.toLocaleString('pt-BR'),
          hourEnd: hourEnd.toLocaleString('pt-BR'),
          isInHour
        });
        
        return isInHour;
      }).length;
      
      data.push({
        time: time.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
        conversations: conversionsInHour, // Usar o mesmo campo para manter compatibilidade
      });
    }
    return data;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return COLORS.success;
      case 'busy': return COLORS.error;
      case 'away': return COLORS.warning;
      case 'offline': return COLORS.secondary;
      default: return COLORS.secondary;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'available': return <CheckCircleIcon />;
      case 'busy': return <ErrorIcon />;
      case 'away': return <WarningIcon />;
      case 'offline': return <VisibilityOffIcon />;
      default: return <PersonIcon />;
    }
  };

  if (loading && !metrics.conversations.incoming) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: { xs: 2, md: 3 }, 
      backgroundColor: 'transparent', 
      minHeight: '100vh',
      color: 'text.primary',
      position: 'relative',
      zIndex: 1,
    }}>
      {/* Header com título e filtros */}
      <Box sx={{ 
        mb: { xs: 0.5, md: 1 }, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', md: 'center' },
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: 0.5, md: 0 },
        padding: { xs: '8px 16px', md: '12px 20px' },
        paddingTop: { xs: '8px', md: '12px' },
        position: 'relative',
        zIndex: 1,
      }}>
        <Box>
        <Typography variant="h5" sx={{ 
          fontWeight: 'bold', 
            color: '#000000',
            fontSize: { xs: '1.25rem', md: '1.5rem' },
            mb: 0,
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
        }}>
            Painel de Conversas
        </Typography>
        </Box>
        
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          width: { xs: '100%', md: 'auto' }
        }}>
          <Tooltip title="Atualizar dados">
            <IconButton onClick={fetchDashboardData} color="primary" size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>




      {/* KPIs Principais - Estilo Tailwind Template */}
      <div className="grid grid-cols-12 gap-4 sm:gap-6 mb-8">
        {/* 1. Taxa de conversão nas últimas 24hrs */}
        <CardSpotlight className="col-span-6 sm:col-span-6 xl:col-span-3">
        <div className="flex flex-col h-full bg-white border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.06)] rounded-2xl overflow-hidden relative transition-all duration-300 ease-out cursor-pointer hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(0,0,0,0.1),0_4px_12px_rgba(20,184,166,0.08)] hover:border-teal-100">
          {/* Decorative medical icon background */}
          <div className="absolute bottom-2 right-2 pointer-events-none z-0 kpi-decorative-icon hidden sm:block" style={{ transform: 'rotate(-10deg)', opacity: 0.6 }}>
            <img 
              src="/Gemini_Generated_Image_l7q9lsl7q9lsl7q9_2-removebg-preview.png" 
              alt="Decorative icon" 
              style={{ width: '120px', height: '120px', objectFit: 'contain' }}
            />
          </div>
          <div className="px-5 pt-5 relative z-10">
            <div className="flex justify-between items-start mb-2">
              <div className="p-2.5 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(20,184,166,0.1)' }}>
                <TrendingUpIcon style={{ fontSize: 28, color: '#0d9488' }} />
              </div>
              <Chip 
                label="24h" 
                size="small" 
                sx={{ backgroundColor: 'rgba(20,184,166,0.1)', color: '#0d9488', border: '1px solid rgba(20,184,166,0.2)', fontWeight: 600, fontSize: '0.7rem' }}
              />
            </div>
            <div className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase mb-1">Taxa de Agendamentos</div>
            <div className="flex items-start mb-4">
              <div className="text-3xl font-bold text-gray-800 dark:text-gray-100 mr-2">
                    {conversionRate24h.toFixed(1)}%
              </div>
              {comparisons.conversionRate24h !== null && (
                <div className={`text-sm font-medium px-1.5 rounded-full ${
                  comparisons.conversionRate24h >= 0 
                    ? 'text-green-600 dark:text-green-400 bg-green-500/20' 
                    : 'text-red-600 dark:text-red-400 bg-red-500/20'
                }`}>
                  {formatPercentageChange(comparisons.conversionRate24h).value}
                </div>
              )}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Últimas 24 horas
            </div>
              <div className="flex items-center justify-between pt-8 pb-5 border-t border-gray-200 dark:border-gray-700/60">
              {comparisons.conversionRate24h !== null ? (
                <>
                <div className="flex items-center gap-1">
                  {comparisons.conversionRate24h >= 0 ? (
                    <TrendingUpIcon className="text-green-600 dark:text-green-400" style={{ fontSize: 16 }} />
                  ) : (
                    <TrendingUpIcon className="text-red-600 dark:text-red-400" style={{ fontSize: 16, transform: 'rotate(180deg)' }} />
                  )}
                  <span className={`text-sm font-medium ${
                    comparisons.conversionRate24h >= 0 
                      ? 'text-green-600 dark:text-green-400' 
                      : 'text-red-600 dark:text-red-400'
                  }`}>
                    {formatPercentageChange(comparisons.conversionRate24h).value} {comparisons.conversionRate24h >= 0 ? '▲' : '▼'}
                  </span>
                </div>
                </>
              ) : (
                <span className="text-xs text-gray-500 dark:text-gray-400">Sem dados para comparação</span>
            )}
            </div>
          </div>
        </div>
        </CardSpotlight>

        {/* 2. Total de Leads */}
        <CardSpotlight className="col-span-6 sm:col-span-6 xl:col-span-3">
        <div className="flex flex-col h-full bg-white border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.06)] rounded-2xl overflow-hidden relative transition-all duration-300 ease-out cursor-pointer hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(0,0,0,0.1),0_4px_12px_rgba(20,184,166,0.08)] hover:border-teal-100">
          {/* Decorative medical icon background */}
          <div className="absolute bottom-2 right-2 pointer-events-none z-0 kpi-decorative-icon hidden sm:block" style={{ transform: 'rotate(-10deg)', opacity: 0.6 }}>
            <img 
              src="/Gemini_Generated_Image_l7q9lsl7q9lsl7q9_3-removebg-preview.png" 
              alt="Decorative icon" 
              style={{ width: '120px', height: '120px', objectFit: 'contain' }}
            />
          </div>
          <div className="px-5 pt-5 relative z-10">
            <div className="flex justify-between items-start mb-2">
              <div className="p-2.5 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(20,184,166,0.1)' }}>
                <PeopleIcon style={{ fontSize: 28, color: '#0d9488' }} />
              </div>
              <Chip 
                label="Total" 
                size="small" 
                sx={{ backgroundColor: 'rgba(20,184,166,0.1)', color: '#0d9488', border: '1px solid rgba(20,184,166,0.2)', fontWeight: 600, fontSize: '0.7rem' }}
              />
            </div>
            <div className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase mb-1">Total de conversas</div>
            <div className="flex items-start mb-4">
              <div className="text-3xl font-bold text-gray-800 dark:text-gray-100 mr-2">
                {totalLeads.toLocaleString()}
              </div>
              {comparisons.totalLeads !== null && (
                <div className={`text-sm font-medium px-1.5 rounded-full ${
                  comparisons.totalLeads >= 0 
                    ? 'text-green-600 dark:text-green-400 bg-green-500/20' 
                    : 'text-red-600 dark:text-red-400 bg-red-500/20'
                }`}>
                  {formatPercentageChange(comparisons.totalLeads).value}
                </div>
              )}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Todos os leads
            </div>
              <div className="flex items-center justify-between pt-8 pb-5 border-t border-gray-200 dark:border-gray-700/60">
              {comparisons.totalLeads !== null ? (
                <>
                <div className="flex items-center gap-1">
                  {comparisons.totalLeads >= 0 ? (
                    <TrendingUpIcon className="text-green-600 dark:text-green-400" style={{ fontSize: 16 }} />
                  ) : (
                    <TrendingUpIcon className="text-red-600 dark:text-red-400" style={{ fontSize: 16, transform: 'rotate(180deg)' }} />
                  )}
                  <span className={`text-sm font-medium ${
                    comparisons.totalLeads >= 0 
                      ? 'text-green-600 dark:text-green-400' 
                      : 'text-red-600 dark:text-red-400'
                  }`}>
                    {formatPercentageChange(comparisons.totalLeads).value} {comparisons.totalLeads >= 0 ? '▲' : '▼'}
                  </span>
                </div>
                </>
              ) : (
                <span className="text-xs text-gray-500 dark:text-gray-400">Sem dados para comparação</span>
            )}
            </div>
          </div>
        </div>
        </CardSpotlight>

        {/* 3. Número de Conversões */}
        <CardSpotlight className="col-span-6 sm:col-span-6 xl:col-span-3">
        <div className="flex flex-col h-full bg-white border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.06)] rounded-2xl overflow-hidden relative transition-all duration-300 ease-out cursor-pointer hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(0,0,0,0.1),0_4px_12px_rgba(20,184,166,0.08)] hover:border-teal-100">
          {/* Decorative medical icon background */}
          <div className="absolute bottom-2 right-2 pointer-events-none z-0 kpi-decorative-icon hidden sm:block" style={{ transform: 'rotate(-10deg)', opacity: 0.6 }}>
            <img 
              src="/Gemini_Generated_Image_l7q9lsl7q9lsl7q9_4-removebg-preview.png" 
              alt="Decorative icon" 
              style={{ width: '120px', height: '120px', objectFit: 'contain' }}
            />
          </div>
          <div className="px-5 pt-5 relative z-10">
            <div className="flex justify-between items-start mb-2">
              <div className="p-2.5 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(20,184,166,0.1)' }}>
                <CheckCircleIcon style={{ fontSize: 28, color: '#0d9488' }} />
              </div>
              <Chip 
                icon={<StarIcon style={{ fontSize: 14, color: '#0d9488' }} />}
                label="Sucesso" 
                size="small" 
                sx={{ backgroundColor: 'rgba(20,184,166,0.1)', color: '#0d9488', border: '1px solid rgba(20,184,166,0.2)', fontWeight: 600, fontSize: '0.7rem' }}
              />
            </div>
            <div className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase mb-1">Agendamentos</div>
            <div className="flex items-start mb-4">
              <div className="text-3xl font-bold text-gray-800 dark:text-gray-100 mr-2">
                {conversionsCount.toLocaleString()}
              </div>
              {comparisons.conversionsCount !== null && (
                <div className={`text-sm font-medium px-1.5 rounded-full ${
                  comparisons.conversionsCount >= 0 
                    ? 'text-green-600 dark:text-green-400 bg-green-500/20' 
                    : 'text-red-600 dark:text-red-400 bg-red-500/20'
                }`}>
                  {formatPercentageChange(comparisons.conversionsCount).value}
                </div>
              )}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Total de conversões
            </div>
              <div className="flex items-center justify-between pt-8 pb-5 border-t border-gray-200 dark:border-gray-700/60">
              {comparisons.conversionsCount !== null ? (
                <>
                <div className="flex items-center gap-1">
                  {comparisons.conversionsCount >= 0 ? (
                    <TrendingUpIcon className="text-green-600 dark:text-green-400" style={{ fontSize: 16 }} />
                  ) : (
                    <TrendingUpIcon className="text-red-600 dark:text-red-400" style={{ fontSize: 16, transform: 'rotate(180deg)' }} />
                  )}
                  <span className={`text-sm font-medium ${
                    comparisons.conversionsCount >= 0 
                      ? 'text-green-600 dark:text-green-400' 
                      : 'text-red-600 dark:text-red-400'
                  }`}>
                    {formatPercentageChange(comparisons.conversionsCount).value} {comparisons.conversionsCount >= 0 ? '▲' : '▼'}
                  </span>
                </div>
                </>
              ) : (
                <span className="text-xs text-gray-500 dark:text-gray-400">Sem dados para comparação</span>
            )}
            </div>
          </div>
        </div>
        </CardSpotlight>

        {/* 4. Leads Hoje */}
        <CardSpotlight className="col-span-6 sm:col-span-6 xl:col-span-3">
        <div className="flex flex-col h-full bg-white border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.06)] rounded-2xl overflow-hidden relative transition-all duration-300 ease-out cursor-pointer hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(0,0,0,0.1),0_4px_12px_rgba(20,184,166,0.08)] hover:border-teal-100">
          {/* Decorative medical icon background */}
          <div className="absolute bottom-2 right-2 pointer-events-none z-0 kpi-decorative-icon hidden sm:block" style={{ transform: 'rotate(-10deg)', opacity: 0.6 }}>
            <img 
              src="/Gemini_Generated_Image_l7q9lsl7q9lsl7q9_5-removebg-preview.png" 
              alt="Decorative icon" 
              style={{ width: '120px', height: '120px', objectFit: 'contain' }}
            />
          </div>
          <div className="px-5 pt-5 relative z-10">
            <div className="flex justify-between items-start mb-2">
              <div className="p-2.5 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(20,184,166,0.1)' }}>
                <ScheduleIcon style={{ fontSize: 28, color: '#0d9488' }} />
              </div>
              <Chip 
                icon={<CalendarIcon style={{ fontSize: 14, color: '#0d9488' }} />}
                label="Hoje" 
                size="small"
                sx={{ backgroundColor: 'rgba(20,184,166,0.1)', color: '#0d9488', border: '1px solid rgba(20,184,166,0.2)', fontWeight: 600, fontSize: '0.7rem' }}
              />
            </div>
            <div className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase mb-1">Novas conversas hoje</div>
            <div className="flex items-start mb-4">
              <div className="text-3xl font-bold text-gray-800 dark:text-gray-100 mr-2">
                {leadsToday.toLocaleString()}
              </div>
              {comparisons.leadsToday !== null && (
                <div className={`text-sm font-medium px-1.5 rounded-full ${
                  comparisons.leadsToday >= 0 
                    ? 'text-green-600 dark:text-green-400 bg-green-500/20' 
                    : 'text-red-600 dark:text-red-400 bg-red-500/20'
                }`}>
                  {formatPercentageChange(comparisons.leadsToday).value}
                </div>
              )}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Leads de hoje
            </div>
              <div className="flex items-center justify-between pt-8 pb-5 border-t border-gray-200 dark:border-gray-700/60">
              {comparisons.leadsToday !== null ? (
                <>
                <div className="flex items-center gap-1">
                  {comparisons.leadsToday >= 0 ? (
                    <TrendingUpIcon className="text-green-600 dark:text-green-400" style={{ fontSize: 16 }} />
                  ) : (
                    <TrendingUpIcon className="text-red-600 dark:text-red-400" style={{ fontSize: 16, transform: 'rotate(180deg)' }} />
                  )}
                  <span className={`text-sm font-medium ${
                    comparisons.leadsToday >= 0 
                      ? 'text-green-600 dark:text-green-400' 
                      : 'text-red-600 dark:text-red-400'
                  }`}>
                    {formatPercentageChange(comparisons.leadsToday).value} {comparisons.leadsToday >= 0 ? '▲' : '▼'}
                  </span>
                </div>
                </>
              ) : (
                <span className="text-xs text-gray-500 dark:text-gray-400">Sem dados para comparação</span>
            )}
            </div>
          </div>
        </div>
        </CardSpotlight>
      </div>

      {/* Segunda linha de KPIs - Estilo Tailwind Template */}
      <div className="grid grid-cols-12 gap-4 sm:gap-6 mb-8">
        {/* 5. Taxa de Conversão - Geral */}
        <CardSpotlight className="col-span-6 sm:col-span-6 xl:col-span-3">
        <div className="flex flex-col h-full bg-white border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.06)] rounded-2xl overflow-hidden relative transition-all duration-300 ease-out cursor-pointer hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(0,0,0,0.1),0_4px_12px_rgba(20,184,166,0.08)] hover:border-teal-100">
          {/* Decorative medical icon background */}
          <div className="absolute bottom-2 right-2 pointer-events-none z-0 kpi-decorative-icon hidden sm:block" style={{ transform: 'rotate(-10deg)', opacity: 0.6 }}>
            <img 
              src="/Gemini_Generated_Image_l7q9lsl7q9lsl7q9_6-removebg-preview.png" 
              alt="Decorative icon" 
              style={{ width: '120px', height: '120px', objectFit: 'contain' }}
            />
          </div>
          <div className="px-5 pt-5 relative z-10">
            <div className="flex justify-between items-start mb-2">
              <div className="p-2.5 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(20,184,166,0.1)' }}>
                <AssessmentIcon style={{ fontSize: 28, color: '#0d9488' }} />
              </div>
            </div>
            <div className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase mb-1">Taxa de Agendamentos</div>
            <div className="flex items-start mb-4">
              <div className="text-3xl font-bold text-gray-800 dark:text-gray-100 mr-2">
                {conversionRate.toFixed(1)}%
              </div>
              {comparisons.conversionRate !== null && (
                <div className={`text-sm font-medium px-1.5 rounded-full ${
                  comparisons.conversionRate >= 0 
                    ? 'text-green-600 dark:text-green-400 bg-green-500/20' 
                    : 'text-red-600 dark:text-red-400 bg-red-500/20'
                }`}>
                  {formatPercentageChange(comparisons.conversionRate).value}
                </div>
              )}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Geral
            </div>
              <div className="flex items-center gap-1 pt-8 pb-5 border-t border-gray-200 dark:border-gray-700/60">
              {comparisons.conversionRate !== null ? (
                <>
                {comparisons.conversionRate >= 0 ? (
                  <TrendingUpIcon className="text-green-600 dark:text-green-400" style={{ fontSize: 16 }} />
                ) : (
                  <TrendingUpIcon className="text-red-600 dark:text-red-400" style={{ fontSize: 16, transform: 'rotate(180deg)' }} />
                )}
                <span className={`text-sm font-medium ${
                  comparisons.conversionRate >= 0 
                    ? 'text-green-600 dark:text-green-400' 
                    : 'text-red-600 dark:text-red-400'
                }`}>
                  {formatPercentageChange(comparisons.conversionRate).value} {comparisons.conversionRate >= 0 ? '▲' : '▼'}
                </span>
                </>
              ) : (
                <span className="text-xs text-gray-500 dark:text-gray-400">Sem dados para comparação</span>
            )}
            </div>
          </div>
        </div>
        </CardSpotlight>

        {/* 6. Leads - Últimas 24 hrs */}
        <CardSpotlight className="col-span-6 sm:col-span-6 xl:col-span-3">
        <div className="flex flex-col h-full bg-white border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.06)] rounded-2xl overflow-hidden relative transition-all duration-300 ease-out cursor-pointer hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(0,0,0,0.1),0_4px_12px_rgba(20,184,166,0.08)] hover:border-teal-100">
          {/* Decorative medical icon background */}
          <div className="absolute bottom-2 right-2 pointer-events-none z-0 kpi-decorative-icon hidden sm:block" style={{ transform: 'rotate(-10deg)', opacity: 0.6 }}>
            <img 
              src="/Gemini_Generated_Image_l7q9lsl7q9lsl7q9_7-removebg-preview.png" 
              alt="Decorative icon" 
              style={{ width: '120px', height: '120px', objectFit: 'contain' }}
            />
          </div>
          <div className="px-5 pt-5 relative z-10">
            <div className="flex justify-between items-start mb-2">
              <div className="p-2.5 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(20,184,166,0.1)' }}>
                <PeopleIcon style={{ fontSize: 28, color: '#0d9488' }} />
              </div>
            </div>
            <div className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase mb-1">Novas conversas</div>
            <div className="flex items-start mb-4">
              <div className="text-3xl font-bold text-gray-800 dark:text-gray-100 mr-2">
                {leads24h.toLocaleString()}
              </div>
              {comparisons.leads24h !== null && (
                <div className={`text-sm font-medium px-1.5 rounded-full ${
                  comparisons.leads24h >= 0 
                    ? 'text-green-600 dark:text-green-400 bg-green-500/20' 
                    : 'text-red-600 dark:text-red-400 bg-red-500/20'
                }`}>
                  {formatPercentageChange(comparisons.leads24h).value}
                </div>
              )}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Últimas 24 horas
            </div>
              <div className="flex items-center gap-1 pt-8 pb-5 border-t border-gray-200 dark:border-gray-700/60">
              {comparisons.leads24h !== null ? (
                <>
                {comparisons.leads24h >= 0 ? (
                  <TrendingUpIcon className="text-green-600 dark:text-green-400" style={{ fontSize: 16 }} />
                ) : (
                  <TrendingUpIcon className="text-red-600 dark:text-red-400" style={{ fontSize: 16, transform: 'rotate(180deg)' }} />
                )}
                <span className={`text-sm font-medium ${
                  comparisons.leads24h >= 0 
                    ? 'text-green-600 dark:text-green-400' 
                    : 'text-red-600 dark:text-red-400'
                }`}>
                  {formatPercentageChange(comparisons.leads24h).value} {comparisons.leads24h >= 0 ? '▲' : '▼'}
                </span>
                </>
              ) : (
                <span className="text-xs text-gray-500 dark:text-gray-400">Sem dados para comparação</span>
            )}
            </div>
          </div>
        </div>
        </CardSpotlight>

        {/* 7. Eventos - Últimas 24 hrs */}
        <CardSpotlight className="col-span-6 sm:col-span-6 xl:col-span-3">
        <div className="flex flex-col h-full bg-white border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.06)] rounded-2xl overflow-hidden relative transition-all duration-300 ease-out cursor-pointer hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(0,0,0,0.1),0_4px_12px_rgba(20,184,166,0.08)] hover:border-teal-100">
          {/* Decorative medical icon background */}
          <div className="absolute bottom-2 right-2 pointer-events-none z-0 kpi-decorative-icon hidden sm:block" style={{ transform: 'rotate(-10deg)', opacity: 0.6 }}>
            <img 
              src="/Gemini_Generated_Image_l7q9lsl7q9lsl7q9_8-removebg-preview.png" 
              alt="Decorative icon" 
              style={{ width: '120px', height: '120px', objectFit: 'contain' }}
            />
          </div>
          <div className="px-5 pt-5 relative z-10">
            <div className="flex justify-between items-start mb-2">
              <div className="p-2.5 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(20,184,166,0.1)' }}>
                <AssessmentIcon style={{ fontSize: 28, color: '#0d9488' }} />
              </div>
            </div>
            <div className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase mb-1">Eventos</div>
            <div className="flex items-start mb-4">
              <div className="text-3xl font-bold text-gray-800 dark:text-gray-100 mr-2">
                {(dashboardStats?.webhook_events_24h || 0).toLocaleString()}
              </div>
              {comparisons.webhookEvents24h !== null && (
                <div className={`text-sm font-medium px-1.5 rounded-full ${
                  comparisons.webhookEvents24h >= 0 
                    ? 'text-green-600 dark:text-green-400 bg-green-500/20' 
                    : 'text-red-600 dark:text-red-400 bg-red-500/20'
                }`}>
                  {formatPercentageChange(comparisons.webhookEvents24h).value}
                </div>
              )}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Últimas 24 horas
            </div>
              <div className="flex items-center gap-1 pt-8 pb-5 border-t border-gray-200 dark:border-gray-700/60">
              {comparisons.webhookEvents24h !== null ? (
                <>
                {comparisons.webhookEvents24h >= 0 ? (
                  <TrendingUpIcon className="text-green-600 dark:text-green-400" style={{ fontSize: 16 }} />
                ) : (
                  <TrendingUpIcon className="text-red-600 dark:text-red-400" style={{ fontSize: 16, transform: 'rotate(180deg)' }} />
                )}
                <span className={`text-sm font-medium ${
                  comparisons.webhookEvents24h >= 0 
                    ? 'text-green-600 dark:text-green-400' 
                    : 'text-red-600 dark:text-red-400'
                }`}>
                  {formatPercentageChange(comparisons.webhookEvents24h).value} {comparisons.webhookEvents24h >= 0 ? '▲' : '▼'}
                </span>
                </>
              ) : (
                <span className="text-xs text-gray-500 dark:text-gray-400">Sem dados para comparação</span>
            )}
            </div>
          </div>
        </div>
        </CardSpotlight>

        {/* 8. Número de Leads Ontem */}
        <CardSpotlight className="col-span-6 sm:col-span-6 xl:col-span-3">
        <div className="flex flex-col h-full bg-white border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.06)] rounded-2xl overflow-hidden relative transition-all duration-300 ease-out cursor-pointer hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(0,0,0,0.1),0_4px_12px_rgba(20,184,166,0.08)] hover:border-teal-100">
          {/* Decorative medical icon background */}
          <div className="absolute bottom-2 right-2 pointer-events-none z-0 kpi-decorative-icon hidden sm:block" style={{ transform: 'rotate(-10deg)', opacity: 0.6 }}>
            <img 
              src="/Gemini_Generated_Image_l7q9lsl7q9lsl7q9_2-removebg-preview.png" 
              alt="Decorative icon" 
              style={{ width: '120px', height: '120px', objectFit: 'contain' }}
            />
          </div>
          <div className="px-5 pt-5 relative z-10">
            <div className="flex justify-between items-start mb-2">
              <div className="p-2.5 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(20,184,166,0.1)' }}>
                <PeopleIcon style={{ fontSize: 28, color: '#0d9488' }} />
              </div>
            </div>
            <div className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase mb-1">Novas conversas ontem</div>
            <div className="flex items-start mb-4">
              <div className="text-3xl font-bold text-gray-800 dark:text-gray-100 mr-2">
                {leadsYesterday.toLocaleString()}
              </div>
              {comparisons.leadsYesterday !== null && (
                <div className={`text-sm font-medium px-1.5 rounded-full ${
                  comparisons.leadsYesterday >= 0 
                    ? 'text-green-600 dark:text-green-400 bg-green-500/20' 
                    : 'text-red-600 dark:text-red-400 bg-red-500/20'
                }`}>
                  {formatPercentageChange(comparisons.leadsYesterday).value}
                </div>
              )}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Ontem
            </div>
              <div className="flex items-center gap-1 pt-8 pb-5 border-t border-gray-200 dark:border-gray-700/60">
              {comparisons.leadsYesterday !== null ? (
                <>
                {comparisons.leadsYesterday >= 0 ? (
                  <TrendingUpIcon className="text-green-600 dark:text-green-400" style={{ fontSize: 16 }} />
                ) : (
                  <TrendingUpIcon className="text-red-600 dark:text-red-400" style={{ fontSize: 16, transform: 'rotate(180deg)' }} />
                )}
                <span className={`text-sm font-medium ${
                  comparisons.leadsYesterday >= 0 
                    ? 'text-green-600 dark:text-green-400' 
                    : 'text-red-600 dark:text-red-400'
                }`}>
                  {formatPercentageChange(comparisons.leadsYesterday).value} {comparisons.leadsYesterday >= 0 ? '▲' : '▼'}
                </span>
                </>
              ) : (
                <span className="text-xs text-gray-500 dark:text-gray-400">Sem dados para comparação</span>
            )}
            </div>
          </div>
        </div>
        </CardSpotlight>
      </div>

      {/* Cards de Faturamento - Estilo Tailwind Template */}
      {config.revenue.showRevenueCards && (() => {
        // Calcular conversões das últimas 24h baseado na taxa de conversão
        const conversions24hCount = Math.round((leads24h * conversionRate24h) / 100);
        const revenue24h = conversions24hCount * (config.revenue.conversionValueBRL || 0);
        const revenueTotal = conversionsCount * (config.revenue.conversionValueBRL || 0);

        return (
          <div className="grid grid-cols-12 gap-4 sm:gap-6 mb-8">
            {/* Card 1: Faturamento Últimas 24 Horas */}
            <CardSpotlight className="col-span-12 sm:col-span-6">
              <div className="flex flex-col h-full bg-white border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.06)] rounded-2xl overflow-hidden relative transition-all duration-300 ease-out cursor-pointer hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(0,0,0,0.1),0_4px_12px_rgba(20,184,166,0.08)] hover:border-teal-100">
                <div className="absolute bottom-[-15px] right-[-15px] opacity-[0.07] pointer-events-none" style={{ transform: 'rotate(-10deg)' }}>
                  <AttachMoneyIcon style={{ fontSize: 140, color: '#0d9488' }} />
                </div>
                <div className="px-5 pt-5 relative z-10">
                  <div className="flex justify-between items-start mb-2">
                    <div className="p-2.5 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(20,184,166,0.1)' }}>
                      <AttachMoneyIcon style={{ fontSize: 28, color: '#0d9488' }} />
                    </div>
                    <Chip 
                      label="24h" 
                      size="small" 
                      sx={{ backgroundColor: 'rgba(20,184,166,0.1)', color: '#0d9488', border: '1px solid rgba(20,184,166,0.2)', fontWeight: 600, fontSize: '0.7rem' }}
                    />
                  </div>
                  <div className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase mb-1">Faturamento</div>
                  <div className="flex items-start mb-4">
                    <div className="text-3xl font-bold text-gray-800 dark:text-gray-100 mr-2">
                      R$ {revenue24h.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Últimas 24 horas
                  </div>
                  <div className="flex items-center justify-between pt-8 pb-5 border-t border-gray-200 dark:border-gray-700/60">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {conversions24hCount} conversões × R$ {(config.revenue.conversionValueBRL || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </CardSpotlight>

            {/* Card 2: Faturamento Total */}
            <CardSpotlight className="col-span-12 sm:col-span-6">
              <div className="flex flex-col h-full bg-white border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.06)] rounded-2xl overflow-hidden relative transition-all duration-300 ease-out cursor-pointer hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(0,0,0,0.1),0_4px_12px_rgba(20,184,166,0.08)] hover:border-teal-100">
                <div className="absolute bottom-[-15px] right-[-15px] opacity-[0.07] pointer-events-none" style={{ transform: 'rotate(-10deg)' }}>
                  <TrendingUpIcon style={{ fontSize: 140, color: '#0d9488' }} />
                </div>
                <div className="px-5 pt-5 relative z-10">
                  <div className="flex justify-between items-start mb-2">
                    <div className="p-2.5 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(20,184,166,0.1)' }}>
                      <AttachMoneyIcon style={{ fontSize: 28, color: '#0d9488' }} />
                    </div>
                    <Chip 
                      label="Total" 
                      size="small" 
                      sx={{ backgroundColor: 'rgba(20,184,166,0.1)', color: '#0d9488', border: '1px solid rgba(20,184,166,0.2)', fontWeight: 600, fontSize: '0.7rem' }}
                    />
                  </div>
                  <div className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase mb-1">Faturamento</div>
                  <div className="flex items-start mb-4">
                    <div className="text-3xl font-bold text-gray-800 dark:text-gray-100 mr-2">
                      R$ {revenueTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Total acumulado
                  </div>
                  <div className="flex items-center justify-between pt-8 pb-5 border-t border-gray-200 dark:border-gray-700/60">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {conversionsCount} conversões × R$ {(config.revenue.conversionValueBRL || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </CardSpotlight>
          </div>
        );
      })()}

      {/* Gráficos - Conversas ao longo do tempo, Agenda e Eventos de Integrações */}
      <div className="grid grid-cols-12 gap-6 mb-8 hidden md:grid">
        {/* Conversas ao longo do tempo - Mais comprimido */}
        <div className="flex flex-col col-span-full sm:col-span-6 xl:col-span-5 bg-white border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.06)] rounded-2xl transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(0,0,0,0.1)]">
          <div className="px-5 pt-5">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-2">
                Conversas ao Longo do Tempo
            </h2>
            <div className="grow max-sm:max-h-[300px] xl:max-h-[300px]">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={conversationData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" className="dark:stroke-gray-700" />
                  <XAxis 
                    dataKey="time" 
                    tick={{ fill: 'currentColor', fontSize: 12 }}
                    className="text-gray-600 dark:text-gray-400"
                  />
                  <YAxis 
                    tick={{ fill: 'currentColor', fontSize: 12 }}
                    className="text-gray-600 dark:text-gray-400"
                  />
                  <RechartsTooltip 
                    contentStyle={{
                      backgroundColor: 'rgba(255,255,255,0.95)',
                      border: '1px solid rgba(0,0,0,0.2)',
                      borderRadius: '8px',
                      color: '#111827',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="conversations"
                    stroke="#14b8a6"
                    strokeWidth={2.5}
                    dot={{ fill: '#14b8a6', r: 3 }}
                    activeDot={{ r: 5, fill: '#0d9488' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Miniatura da Agenda */}
        <div className="flex flex-col col-span-full sm:col-span-6 xl:col-span-3 bg-white border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.06)] rounded-2xl transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(0,0,0,0.1)]">
          <div className="px-5 pt-5 flex-1 flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                Agenda
              </h2>
              <IconButton 
                size="small" 
                onClick={() => navigate('/agenda')}
                style={{ color: '#0d9488' }}
              >
                <CalendarIcon fontSize="small" />
              </IconButton>
            </div>
            <div className="flex-1 overflow-y-auto max-h-[300px] no-scrollbar">
              <div className="space-y-0">
                {agendaEvents.length > 0 ? (
                  agendaEvents.map((event, index) => {
                    // Formatar data e hora
                    const eventDate = new Date(event.event_date + 'T' + event.start_time);
                    const now = new Date();
                    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                    const tomorrow = new Date(today);
                    tomorrow.setDate(tomorrow.getDate() + 1);
                    
                    let dateLabel = '';
                    if (eventDate >= today && eventDate < tomorrow) {
                      dateLabel = 'Hoje';
                    } else if (eventDate >= tomorrow && eventDate < new Date(tomorrow.getTime() + 24 * 60 * 60 * 1000)) {
                      dateLabel = 'Amanhã';
                    } else {
                      dateLabel = eventDate.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
                    }
                    
                    // Formatar hora
                    const timeLabel = event.start_time ? event.start_time.substring(0, 5) : '';
                    
                    // Mapear tipo de evento para ícone
                    const getEventIcon = (type: string) => {
                      switch (type) {
                        case 'meeting':
                          return 'R';
                        case 'call':
                          return 'C';
                        case 'task':
                          return 'T';
                        case 'reminder':
                          return 'L';
                        default:
                          return 'E';
                      }
                    };
                    
                    const getEventColor = (type: string) => {
                      switch (type) {
                        case 'meeting':
                          return 'bg-teal-500';
                        case 'call':
                          return 'bg-green-500';
                        case 'task':
                          return 'bg-orange-500';
                        case 'reminder':
                          return 'bg-teal-600';
                        default:
                          return 'bg-gray-500';
                      }
                    };
                    
                    return (
                      <React.Fragment key={event.id}>
                        <div className="flex items-center py-2">
                          <Avatar className={`w-8 h-8 text-xs ${getEventColor(event.event_type)}`}>
                            {getEventIcon(event.event_type)}
                          </Avatar>
                          <div className="ml-3 flex-1">
                            <Typography variant="body2" className="font-medium text-sm text-gray-800 dark:text-gray-100">
                              {event.title}
                            </Typography>
                            <Typography variant="caption" className="text-xs text-gray-500 dark:text-gray-400">
                              {timeLabel} • {dateLabel}
                            </Typography>
                          </div>
                        </div>
                        {index < agendaEvents.length - 1 && <hr className="border-gray-200 dark:border-gray-700/60" />}
                      </React.Fragment>
                    );
                  })
                ) : (
                  <div className="flex items-center justify-center py-8">
                    <Typography variant="body2" className="text-gray-500 dark:text-gray-400 text-center">
                      Nenhum evento agendado
                    </Typography>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Últimos Eventos de Integrações */}
        <div className="flex flex-col col-span-full sm:col-span-6 xl:col-span-4 bg-white border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.06)] rounded-2xl transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(0,0,0,0.1)]">
          <div className="px-5 pt-5 flex-1 flex flex-col">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
              Últimos Eventos de Integrações
            </h2>
            <div className="flex-1 overflow-y-auto max-h-[300px] no-scrollbar">
              <div className="space-y-0">
                {webhookLogs.length > 0 ? (
                  webhookLogs.slice(0, 4).map((log, index) => {
                    // Formatar nome do provider
                    const getProviderName = (source: string) => {
                      const names: Record<string, string> = {
                        'hubla': 'Hubla',
                        'kirvano': 'Kirvano',
                        'hotmart': 'Hotmart',
                        'kiwify': 'Kiwify',
                        'braip': 'Braip',
                        'perfectpay': 'PerfectPay'
                      };
                      return names[source?.toLowerCase()] || source || 'Desconhecido';
                    };

                    // Formatar tempo relativo
                    const formatTimeAgo = (dateString: string) => {
                      const date = new Date(dateString);
                      const now = new Date();
                      const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

                      if (diffInSeconds < 60) {
                        return 'agora mesmo';
                      } else if (diffInSeconds < 3600) {
                        const minutes = Math.floor(diffInSeconds / 60);
                        return `há ${minutes} min`;
                      } else if (diffInSeconds < 86400) {
                        const hours = Math.floor(diffInSeconds / 3600);
                        return `há ${hours} hora${hours > 1 ? 's' : ''}`;
                      } else {
                        const days = Math.floor(diffInSeconds / 86400);
                        return `há ${days} dia${days > 1 ? 's' : ''}`;
                      }
                    };

                    // Extrair nome do cliente/evento
                    const getEventName = (log: any) => {
                      // Tentar extrair nome de diferentes campos possíveis
                      const name = log.customer_name || log.name || log.buyer_name || log.client_name || 
                                   log.data?.customer_name || log.data?.name || log.data?.buyer_name ||
                                   log.payload?.customer_name || log.payload?.name || log.payload?.buyer_name ||
                                   'Cliente';
                      
                      return name;
                    };

                    // Extrair tipo de evento
                    const getEventType = (log: any) => {
                      return log.event_type || log.event || log.type || log.data?.event_type || 
                             log.data?.event || log.payload?.event_type || log.payload?.event || 'event';
                    };

                    // Determinar status baseado no tipo de evento
                    const getEventStatus = (eventType: string) => {
                      const type = eventType?.toLowerCase() || '';
                      if (type.includes('approved') || type.includes('complete') || type.includes('success') || 
                          type.includes('payment_succeeded') || type.includes('activated')) {
                        return 'success';
                      } else if (type.includes('canceled') || type.includes('refunded') || type.includes('error') || 
                                 type.includes('failed') || type.includes('expired')) {
                        return 'error';
                      } else if (type.includes('abandoned') || type.includes('out_of') || type.includes('pending')) {
                        return 'warning';
                      }
                      return 'success';
                    };

                    // Extrair valor se disponível
                    const getEventValue = (log: any) => {
                      const value = log.value || log.amount || log.price || log.total || 
                                   log.data?.value || log.data?.amount || log.data?.price || log.data?.total ||
                                   log.payload?.value || log.payload?.amount || log.payload?.price || log.payload?.total;
                      
                      if (value) {
                        return `R$ ${parseFloat(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                      }
                      return null;
                    };

                    const providerName = getProviderName(log.source);
                    const eventType = getEventType(log);
                    const eventName = getEventName(log);
                    const eventStatus = getEventStatus(eventType);
                    const eventValue = getEventValue(log);
                    const timeAgo = formatTimeAgo(log.processed_at || log.created_at || new Date().toISOString());

                    const getProviderIcon = (provider: string) => {
                      const icons: Record<string, string> = {
                        'Hotmart': 'H',
                        'Hubla': 'Hu',
                        'Kirvano': 'K',
                        'Kiwify': 'Ki',
                        'Braip': 'B',
                        'PerfectPay': 'P'
                      };
                      return icons[provider] || provider.charAt(0);
                    };

                    const getEventLabel = (eventType: string) => {
                      const labels: Record<string, string> = {
                        'PURCHASE_APPROVED': 'Compra Aprovada',
                        'PURCHASE_COMPLETE': 'Compra Completa',
                        'PURCHASE_OUT_OF_SHOPPING_CART': 'Carrinho Abandonado',
                        'PURCHASE_CANCELED': 'Compra Cancelada',
                        'PURCHASE_EXPIRED': 'Compra Expirada',
                        'PURCHASE_REFUNDED': 'Reembolso',
                        'invoice.payment_succeeded': 'Pagamento Aprovado',
                        'subscription.activated': 'Assinatura Ativada',
                        'SALE_APPROVED': 'Venda Aprovada',
                        'order.approved': 'Pedido Aprovado',
                        'order.completed': 'Pedido Completo'
                      };
                      return labels[eventType] || eventType;
                    };

                    return (
                      <React.Fragment key={log.id || index}>
                        <div className="flex items-center py-2">
                          <Avatar className={`w-8 h-8 text-xs ${
                            eventStatus === 'success' ? 'bg-green-500' : 
                            eventStatus === 'warning' ? 'bg-orange-500' : 'bg-red-500'
                          }`}>
                            {getProviderIcon(providerName)}
                          </Avatar>
                          <div className="ml-3 flex-1">
                            <Typography variant="body2" className="font-medium text-sm text-gray-800 dark:text-gray-100">
                              {getEventLabel(eventType)} - {eventName}
                            </Typography>
                            <Typography variant="caption" className="text-xs text-gray-500 dark:text-gray-400">
                              {timeAgo} {eventValue && `• ${eventValue}`}
                            </Typography>
                          </div>
                          <Chip
                            label={eventStatus === 'success' ? 'OK' : eventStatus === 'warning' ? 'Aviso' : 'Erro'}
                            size="small"
                            className={`text-xs h-5 ${
                              eventStatus === 'success' ? 'bg-green-500/20 text-green-700 dark:text-green-400' : 
                              eventStatus === 'warning' ? 'bg-orange-500/20 text-orange-700 dark:text-orange-400' : 
                              'bg-red-500/20 text-red-700 dark:text-red-400'
                            }`}
                          />
                        </div>
                        {index < Math.min(webhookLogs.length, 4) - 1 && <hr className="border-gray-200 dark:border-gray-700/60" />}
                      </React.Fragment>
                    );
                  })
                ) : (
                  <div className="flex items-center justify-center py-8">
                    <Typography variant="body2" className="text-gray-500 dark:text-gray-400 text-center">
                      Nenhum evento de integração recente
                    </Typography>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Segunda linha: Estágios do CRM - Gráfico de Linha - Oculto no mobile */}
      <div className="grid grid-cols-12 gap-6 mb-8 hidden md:grid">
        {/* Estágios do CRM */}
        <div className="flex flex-col col-span-full bg-white border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.06)] rounded-2xl transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(0,0,0,0.1)]">
          <div className="px-5 pt-5">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
              Leads por Estágio do CRM
            </h2>
            <div className="grow max-sm:max-h-[400px] xl:max-h-[400px]">
              <ResponsiveContainer width="100%" height={400}>
                {(() => {
                  // Buscar dados reais dos estágios do CRM
                  const stageData = crmStats?.stageStats || [];
                  
                  // Se temos dados de estágios, criar gráfico de barras horizontais
                  if (stageData.length > 0) {
                    // Obter todos os estágios com dados
                    const allStages = stageData
                      .filter((stage: any) => stage.name && stage.count !== undefined)
                      .map((stage: any) => ({
                        name: stage.name,
                        count: stage.count || 0,
                        color: stage.color || '#6366f1'
                      }))
                      .sort((a: any, b: any) => b.count - a.count); // Ordenar por quantidade (maior primeiro)
                    
                    // Cores padrão com gradientes
                    const defaultColors = [
                      { start: '#8b5cf6', end: '#7c3aed' }, // Roxo
                      { start: '#10b981', end: '#059669' }, // Verde
                      { start: '#f59e0b', end: '#d97706' }, // Laranja
                      { start: '#8b5cf6', end: '#7c3aed' }, // Roxo
                      { start: '#ef4444', end: '#dc2626' }, // Vermelho
                      { start: '#06b6d4', end: '#0891b2' }, // Ciano
                      { start: '#ec4899', end: '#db2777' }, // Rosa
                      { start: '#14b8a6', end: '#0d9488' }, // Teal
                    ];
                    
                    // Preparar dados para o gráfico
                    const chartData = allStages.map((stage: any, index: number) => ({
                      name: stage.name.length > 15 ? stage.name.substring(0, 15) + '...' : stage.name,
                      fullName: stage.name,
                      value: stage.count,
                      color: stage.color || defaultColors[index % defaultColors.length].start,
                      gradientId: `gradient-${index}`
                    }));
                    
                    return (
                      <BarChart 
                        data={chartData} 
                        layout="vertical"
                        margin={{ top: 20, right: 30, left: 100, bottom: 20 }}
                      >
                        <defs>
                          {chartData.map((item: any, index: number) => {
                            const colorIndex = index % defaultColors.length;
                            const colors = defaultColors[colorIndex];
                            return (
                              <linearGradient key={item.gradientId} id={item.gradientId} x1="0" y1="0" x2="1" y2="0">
                                <stop offset="0%" stopColor={colors.start} stopOpacity={0.9} />
                                <stop offset="100%" stopColor={colors.end} stopOpacity={0.9} />
                              </linearGradient>
                            );
                          })}
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" className="dark:stroke-gray-700" />
                        <XAxis 
                          type="number"
                          tick={{ fill: 'currentColor', fontSize: 12 }}
                          className="text-gray-600 dark:text-gray-400"
                        />
                        <YAxis 
                          type="category"
                          dataKey="name"
                          tick={{ fill: 'currentColor', fontSize: 12 }}
                          className="text-gray-600 dark:text-gray-400"
                          width={90}
                        />
                        <RechartsTooltip 
                          formatter={(value: any, name: any, props: any) => [
                            `${value} leads`,
                            props.payload.fullName
                          ]}
                          contentStyle={{
                            backgroundColor: 'rgba(255,255,255,0.95)',
                            border: '1px solid rgba(0,0,0,0.2)',
                            borderRadius: '8px',
                            color: '#111827',
                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                            padding: '8px 12px'
                          }}
                        />
                        <Bar 
                          dataKey="value" 
                          radius={[0, 8, 8, 0]}
                        >
                          {chartData.map((entry: any, index: number) => (
                            <Cell key={`cell-${index}`} fill={`url(#${entry.gradientId})`} />
                        ))}
                        </Bar>
                      </BarChart>
                    );
                  }
                  
                  // Fallback: usar stageCounts se disponível
                  if (crmStats?.stageCounts) {
                    const stageNames = ['Novo Lead', 'Grupo', 'Cadastro', 'Ganho'];
                    const stageCounts = [
                      crmStats.stageCounts[1] || 0,
                      crmStats.stageCounts[2] || 0,
                      crmStats.stageCounts[3] || 0,
                      crmStats.stageCounts[4] || 0
                    ];
                    
                    const defaultColors = [
                      { start: '#8b5cf6', end: '#7c3aed' },
                      { start: '#10b981', end: '#059669' },
                      { start: '#f59e0b', end: '#d97706' },
                      { start: '#8b5cf6', end: '#7c3aed' },
                    ];
                    
                    const chartData = stageNames.map((name, index) => ({
                      name: name,
                      value: stageCounts[index],
                      gradientId: `gradient-fallback-${index}`
                    }));
                    
                    return (
                      <BarChart 
                        data={chartData} 
                        layout="vertical"
                        margin={{ top: 20, right: 30, left: 100, bottom: 20 }}
                      >
                        <defs>
                          {chartData.map((item: any, index: number) => {
                            const colors = defaultColors[index];
                            return (
                              <linearGradient key={item.gradientId} id={item.gradientId} x1="0" y1="0" x2="1" y2="0">
                                <stop offset="0%" stopColor={colors.start} stopOpacity={0.9} />
                                <stop offset="100%" stopColor={colors.end} stopOpacity={0.9} />
                              </linearGradient>
                            );
                          })}
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" className="dark:stroke-gray-700" />
                        <XAxis 
                          type="number"
                          tick={{ fill: 'currentColor', fontSize: 12 }}
                          className="text-gray-600 dark:text-gray-400"
                        />
                        <YAxis 
                          type="category"
                          dataKey="name"
                          tick={{ fill: 'currentColor', fontSize: 12 }}
                          className="text-gray-600 dark:text-gray-400"
                          width={90}
                        />
                        <RechartsTooltip 
                          formatter={(value: any) => [`${value} leads`, '']}
                          contentStyle={{
                            backgroundColor: 'rgba(255,255,255,0.95)',
                            border: '1px solid rgba(0,0,0,0.2)',
                            borderRadius: '8px',
                            color: '#111827',
                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                            padding: '8px 12px'
                          }}
                        />
                        <Bar 
                          dataKey="value" 
                          radius={[0, 8, 8, 0]}
                        >
                          {chartData.map((entry: any, index: number) => (
                            <Cell key={`cell-${index}`} fill={`url(#${entry.gradientId})`} />
                        ))}
                        </Bar>
                      </BarChart>
                    );
                  }
                  
                  // Se não há dados, mostrar gráfico vazio
                  return (
                    <div className="flex items-center justify-center h-full">
                      <Typography variant="body2" className="text-gray-500 dark:text-gray-400">
                        Carregando dados dos estágios...
                      </Typography>
                    </div>
                  );
                })()}
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </Box>
  );
};

const Dashboard = Analytics;
export default Dashboard;